import React, { useEffect, useState, useMemo } from 'react';
import { X, ArrowRight, ArrowLeft, Ship, Plane, Truck, Train, Send, MapPin, Package, Clock, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Language } from '../types';
import { CountrySelect } from './CountrySelect';

interface QuoteRequestProps {
  onClose: () => void;
  lang: Language;
}

type FreightMode = 'sea' | 'air' | 'road' | 'rail' | 'not-sure';
type LocationType = 'door' | 'airport' | 'seaport' | 'rail';
type VolumeInputType = 'dimensions' | 'total';
type Priority = 'cheapest' | 'balanced' | 'fastest';
type ContainerSize = '20ft' | '40ft' | '40hc';

// Planning/price-check additions
type QuoteIntent = 'accurate' | 'price-check';
type Frequency = 'one-off' | 'weekly' | 'twice-monthly' | 'monthly' | 'ad-hoc';
type SizeUnitMode = 'buckets' | 'known';
type WeightBucket = '0-5' | '5-20' | '20-50' | '50-100' | '100-300' | '300-1000' | '1000+';
type VolumeBucket = '<0.1' | '0.1-0.5' | '0.5-1' | '1-3' | '3-8' | '8-15' | '15+';
type Lane = {
  originCountry: string;
  originCity: string;
  originType: LocationType;
  destinationCountry: string;
  destinationCity: string;
  destinationType: LocationType;
};
type CargoCategory = 'general' | 'cosmetics' | 'vehicles' | 'machinery' | 'construction' | 'project' | 'dangerous' | 'other';
type VehicleType = 'car' | '4x4' | 'van' | 'truck' | 'motorbike';
// Step 0 - Business profile type
type BusinessProfile =
  | 'construction'
  | 'car-trader'
  | 'electronics'
  | 'fashion'
  | 'cosmetics'
  | 'food'
  | 'courier'
  | 'personal'
  | 'other';

type VehicleEntry = {
  vehicleType: VehicleType;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vehicleQuantity: string;
  vehicleRunningCondition: 'running' | 'non-running' | '';
  vehicleRoRoOrContainer: 'roro' | 'container' | 'not-sure' | '';
  vehicleDimsLength?: string;
  vehicleDimsWidth?: string;
  vehicleDimsHeight?: string;
  vehicleWeightKg?: string;
};

type MachineryItem = {
  equipmentType: string;
  equipmentQuantity: string;
  equipmentCondition: 'new' | 'used' | '';
  equipmentPieces: string;
  equipmentPieceRows: Array<{ length: string; width: string; height: string; weight: string }>;
  equipmentRequiresForklift: boolean;
  equipmentRequiresCrane: boolean;
  equipmentCanBeDismantled: boolean;
};

type ConstructionItem = {
  constructionMaterialType: 'tiles' | 'steel' | 'pipes' | 'cement' | 'timber' | 'other' | '';
  constructionPackaging: 'loose' | 'bundles' | 'pallets' | 'big-bags' | '';
  constructionTotalWeight: string;
  constructionVolumeInputType: VolumeInputType;
  constructionPackages: Array<{ length: string; width: string; height: string; qty: string }>;
  constructionTotalVolume: string;
  constructionLongestItemLength: string;
  constructionWeatherSensitive: boolean;
};

type DangerousItemType = 'battery' | 'liquid' | 'pressurized' | 'other';
type DangerousItem = { type: DangerousItemType; quantity: string; note?: string; customType?: string };

type SimpleItem = {
  commodity: string;
  packagingType: string;
  numberOfPackages: string;
  totalWeight: string;
  volumeInputType: VolumeInputType;
  generalPackages: Array<{ length: string; width: string; height: string; qty: string }>;
  totalVolume: string;
  stackable: boolean;
  fragile: boolean;
};

interface QuoteFormData {
  // Global quote intent
  quoteIntent: QuoteIntent;
   // Step 0 - Who are you?
   businessProfile: BusinessProfile | '';
   businessProfileOther: string;
  shipmentFrequency: 'one-off' | 'regular' | '';

  // Planning mode fields
  lanes: Lane[];
  shippingFrequency: Frequency;
  shipmentsPerMonth: string; // optional
  sizeInputMode: SizeUnitMode;
  typicalWeightBucket: WeightBucket | '';
  typicalVolumeBucket: VolumeBucket | '';
  typicalContainer: '' | '20ft' | '40ft' | '40hc';
  timeSensitivity: 'flexible' | 'fast' | 'deadline';
  deadlineDate: string;

   // Step 1 - Route
   originCountry: string;
   originCity: string;
   originType: LocationType;
   destinationCountry: string;
   destinationCity: string;
   destinationType: LocationType;
   deliveryScope: string;
   // preferredRoute removed from UI
   
   // Service scope (3 key toggles)
   pickupNeeded: 'yes' | 'no' | 'not-sure';
   deliveryNeeded: 'yes' | 'no' | 'not-sure';
   customsClearanceNeeded: 'yes' | 'no' | 'not-sure';
  adviseMeOnServices: boolean;
  // Optional services toggles (collapsible)
  insurance?: boolean;
  crating?: boolean;
  warehousing?: boolean;
  photoInspection?: boolean;
  specialHandling?: boolean;
  craneService?: boolean;
  lowbedService?: boolean;
  
  // Step 2 - Freight Mode
  freightMode: FreightMode;
  seaType: 'lcl' | 'fcl' | '';
  containerSize: ContainerSize;
  hasDangerousGoods: boolean;
  truckType: string;
  forkliftAvailable: boolean;
  
  // Step 3 - Cargo Details
  cargoCategory: CargoCategory;
  commodity: string;
  packagingType: string;
  numberOfPackages: string;
  totalWeight: string;
  volumeInputType: VolumeInputType;
  // General cargo: repeatable package rows
  generalPackages: Array<{ length: string; width: string; height: string; qty: string }>;
  totalVolume: string;
  cargoValue: string;
  stackable: boolean;
  fragile: boolean;

  // Vehicles list
  vehicles: VehicleEntry[];
  
  // Machinery/Equipment list
  machineryItems: MachineryItem[];
  
  // Project cargo (oversize/heavy lift)
  projectOversize: 'yes' | 'no' | 'not-sure' | '';
  projectPieceRows: Array<{ length: string; width: string; height: string; weight: string }>;
  projectHandlingCrane: boolean;
  projectHandlingLowbed: boolean;
  projectPermitsEscort: 'yes' | 'no' | 'not-sure' | '';
  projectSiteConstraints: string;
  
  // Construction materials list + legacy single group (for now)
  constructionItems: ConstructionItem[];
  constructionMaterialType: 'tiles' | 'steel' | 'pipes' | 'cement' | 'timber' | 'other' | '';
  constructionPackaging: 'loose' | 'bundles' | 'pallets' | 'big-bags' | '';
  constructionTotalWeight: string;
  constructionVolumeInputType: VolumeInputType;
  constructionPackages: Array<{ length: string; width: string; height: string; qty: string }>;
  constructionTotalVolume: string;
  constructionLongestItemLength: string;
  
  // (Removed generic oversized section – covered by Project cargo)
  
  // Dangerous / batteries / chemicals
  dangerousAnyBatteries: 'no' | 'yes' | 'not-sure' | '';
  dangerousAnyLiquids: 'no' | 'yes' | 'not-sure' | '';
  dangerousAnyPressurized: 'no' | 'yes' | 'not-sure' | '';
  dangerousSDSAvailable: boolean;
  dangerousItems: DangerousItem[];
  
  // Other category
  otherCategoryDescription: string;
  
  // Step 4 removed (service scope/extras)
  
  // Step 5 - Timing
  cargoReadyDate: string;
  priority: Priority;
  targetDeliveryDate: string;
  
  // Step 6 - Contact
  fullName: string;
  company: string;
  phone: string;
  email: string;
  notes: string;

  // Multi-item support
  generalItems: SimpleItem[];
  cosmeticsItems: SimpleItem[];
}

// Cargo categories list to filter based on profile
const ALL_CARGO_CATEGORIES: CargoCategory[] = [
  'general','cosmetics','vehicles','machinery','construction','project','dangerous','other'
];

// Profile configuration
const PROFILE_CONFIG: Record<BusinessProfile, {
  label: { en: string; ar: string };
  hint?: { en: string; ar: string };
  allowedCargoCategories: CargoCategory[];
  defaults: Partial<QuoteFormData>;
  commoditySuggestions?: { en: string[]; ar: string[] };
}> = {
  construction: {
    label: { en: 'Construction / Contracting', ar: 'مقاولات / إنشاءات' },
    hint: { en: 'Best for materials, equipment, and site deliveries.', ar: 'مناسب لمواد البناء والمعدات والتسليم للموقع.' },
    allowedCargoCategories: ['construction','machinery','project','general'],
    defaults: {
      cargoCategory: 'construction',
      freightMode: 'sea',
      seaType: '',
      packagingType: 'Pallets',
    },
    commoditySuggestions: {
      en: ['Tiles', 'Steel', 'Pipes', 'Cement', 'Timber', 'Construction tools'],
      ar: ['بلاط', 'حديد', 'أنابيب', 'أسمنت', 'خشب', 'معدات بناء']
    }
  },
  'car-trader': {
    label: { en: 'Car dealer / Vehicle trader', ar: 'تاجر سيارات / مركبات' },
    allowedCargoCategories: ['vehicles','project'],
    defaults: {
      cargoCategory: 'vehicles',
      freightMode: 'sea',
      seaType: '',
    },
    commoditySuggestions: {
      en: ['Cars', 'Motorbikes', 'Spare parts'],
      ar: ['سيارات', 'دراجات نارية', 'قطع غيار']
    }
  },
  electronics: {
    label: { en: 'Electronics seller / wholesaler', ar: 'إلكترونيات / تاجر جملة' },
    allowedCargoCategories: ['general','dangerous'],
    defaults: {
      cargoCategory: 'general',
      packagingType: 'Cartons',
      fragile: true,
      dangerousAnyBatteries: 'not-sure',
    },
    commoditySuggestions: {
      en: ['Phones', 'Laptops', 'Accessories', 'TVs', 'Small appliances'],
      ar: ['هواتف', 'لابتوبات', 'إكسسوارات', 'تلفزيونات', 'أجهزة منزلية صغيرة']
    }
  },
  fashion: {
    label: { en: 'Clothing / Fashion', ar: 'ملابس / أزياء' },
    allowedCargoCategories: ['general'],
    defaults: {
      cargoCategory: 'general',
      packagingType: 'Cartons',
      freightMode: 'sea',
      seaType: '',
      stackable: true,
      fragile: false,
    },
    commoditySuggestions: {
      en: ['Clothes', 'Shoes', 'Bags', 'Textiles'],
      ar: ['ملابس', 'أحذية', 'شنط', 'منسوجات']
    }
  },
  cosmetics: {
    label: { en: 'Cosmetics / Makeup', ar: 'مستحضرات تجميل' },
    allowedCargoCategories: ['cosmetics'],
    defaults: {
      cargoCategory: 'cosmetics',
      packagingType: 'Cartons',
      fragile: true,
      dangerousAnyLiquids: 'not-sure',
      dangerousAnyPressurized: 'not-sure',
      dangerousAnyBatteries: 'no',
      freightMode: 'sea',
      seaType: '',
    },
    commoditySuggestions: {
      en: ['Makeup', 'Skincare', 'Perfume'],
      ar: ['مكياج', 'عناية بالبشرة', 'عطور']
    }
  },
  food: {
    label: { en: 'Food / Grocery', ar: 'مواد غذائية' },
    allowedCargoCategories: ['general','construction'],
    defaults: {
      cargoCategory: 'general',
      packagingType: 'Cartons',
    },
    commoditySuggestions: {
      en: ['Dry food', 'Canned goods', 'Snacks', 'Frozen (if applicable)'],
      ar: ['مواد جافة', 'معلبات', 'سناكات', 'مجمدات (إذا ينطبق)']
    }
  },
  courier: {
    label: { en: 'Courier / Frequent shipments', ar: 'شركة شحن / شحنات متكررة' },
    allowedCargoCategories: ['general'],
    defaults: {
      cargoCategory: 'general',
      freightMode: 'air',
      packagingType: 'Cartons',
    }
  },
  personal: {
    label: { en: 'Personal / One-time shipment', ar: 'شخصي / شحنة واحدة' },
    allowedCargoCategories: ALL_CARGO_CATEGORIES,
    defaults: {
      freightMode: 'not-sure',
      adviseMeOnServices: true,
    }
  },
  other: {
    label: { en: 'Other', ar: 'أخرى' },
    allowedCargoCategories: ALL_CARGO_CATEGORIES,
    defaults: { cargoCategory: 'other' }
  }
};

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia', 'Botswana', 'Brazil', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Chad', 'Chile', 'China', 'Colombia', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Estonia', 'Ethiopia', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala',
  'Guinea', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Libya', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Nigeria', 'North Korea', 'Norway', 'Oman', 'Pakistan', 'Panama', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sudan', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const PACKAGING_TYPES = ['Cartons', 'Pallets', 'Crates', 'Loose'];
const TRUCK_TYPES = ['Box Truck', 'Flatbed', 'Refrigerated', 'Tanker', 'Open Truck'];
const COSMETIC_TYPES = ['Makeup', 'Skincare', 'Perfume', 'Aerosol/Spray', 'Haircare', 'Nail polish'];

export const QuoteRequest: React.FC<QuoteRequestProps> = ({ onClose, lang }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // dynamic steps: accurate = 6, price-check = 4
  const [step, setStep] = useState(1);
  const isRTL = lang === 'ar';
  const [optionalSectionOpen, setOptionalSectionOpen] = useState(false);

  const [formData, setFormData] = useState<QuoteFormData>({
    // Intent
    quoteIntent: 'accurate',
    // Step 0 - Who are you?
    businessProfile: '',
    businessProfileOther: '',
    shipmentFrequency: '',

    // Planning mode defaults
    lanes: [{
      originCountry: '', originCity: '', originType: 'door',
      destinationCountry: '', destinationCity: '', destinationType: 'door'
    }],
    shippingFrequency: 'one-off',
    shipmentsPerMonth: '',
    sizeInputMode: 'buckets',
    typicalWeightBucket: '',
    typicalVolumeBucket: '',
    typicalContainer: '',
    timeSensitivity: 'flexible',
    deadlineDate: '',

    // Step 1
    originCountry: '',
    originCity: '',
    originType: 'door',
    destinationCountry: '',
    destinationCity: '',
    destinationType: 'door',
    deliveryScope: '',
    // preferredRoute removed
    
    // Service scope
    pickupNeeded: 'not-sure',
    deliveryNeeded: 'not-sure',
    customsClearanceNeeded: 'not-sure',
    adviseMeOnServices: false,
    insurance: false,
    crating: false,
    warehousing: false,
    photoInspection: false,
    specialHandling: false,
    craneService: false,
    lowbedService: false,
    
    // Step 2
    freightMode: 'not-sure',
    seaType: '',
    containerSize: '20ft',
    hasDangerousGoods: false,
    truckType: '',
    forkliftAvailable: false,
    
    // Step 3
    cargoCategory: 'general',
    commodity: '',
    packagingType: '',
    numberOfPackages: '',
    totalWeight: '',
    volumeInputType: 'dimensions',
    generalPackages: [],
    totalVolume: '',
    cargoValue: '',
    stackable: true,
    fragile: false,

    // Vehicles list
    vehicles: [],
    
    // Machinery list
    machineryItems: [],
    
    // Project cargo fields
    projectOversize: '',
    projectPieceRows: [],
    projectHandlingCrane: false,
    projectHandlingLowbed: false,
    projectPermitsEscort: '',
    projectSiteConstraints: '',
    
    // Construction materials list
    constructionItems: [],
    // Legacy single group (kept for now)
    constructionMaterialType: '',
    constructionPackaging: '',
    constructionTotalWeight: '',
    constructionVolumeInputType: 'dimensions',
    constructionPackages: [],
    constructionTotalVolume: '',
    constructionLongestItemLength: '',
    
    // Removed generic oversized items
    
    // Dangerous goods fields
    dangerousAnyBatteries: '',
    dangerousAnyLiquids: '',
    dangerousAnyPressurized: '',
    dangerousSDSAvailable: false,
    dangerousItems: [],
    
    // Other category
    otherCategoryDescription: '',
    
    // Step 4 removed
    
    // Step 5
    cargoReadyDate: '',
    priority: 'balanced',
    targetDeliveryDate: '',
    
    // Step 6
    fullName: '',
    company: '',
    phone: '',
    email: '',
    notes: '',

    // Multi-item arrays
    generalItems: [],
    cosmeticsItems: [],
  });

  // compute total steps based on chosen intent
  const totalSteps = (formData.quoteIntent === 'accurate') ? 6 : 4;

  // Keep step within bounds when intent changes (after state is defined)
  useEffect(() => {
    const max = formData.quoteIntent === 'price-check' ? 4 : 6;
    if (step > max) setStep(max);
    if (step < 1) setStep(1);
  }, [formData.quoteIntent, step]);

  // Auto-calculate delivery scope
  useEffect(() => {
    const getScopeLabel = () => {
      const origin = formData.originType === 'door' ? 'Door' : formData.originType === 'airport' ? 'Airport' : formData.originType === 'seaport' ? 'Port' : 'Rail';
      const dest = formData.destinationType === 'door' ? 'Door' : formData.destinationType === 'airport' ? 'Airport' : formData.destinationType === 'seaport' ? 'Port' : 'Rail';
      return `${origin}-to-${dest}`;
    };
    setFormData(prev => ({ ...prev, deliveryScope: getScopeLabel() }));
  }, [formData.originType, formData.destinationType]);

  // Auto-calculate CBM from dimensions (general cargo)
  const calculatedVolume = useMemo(() => {
    if (formData.volumeInputType === 'dimensions') {
      const sum = (formData.generalPackages || []).reduce((acc, row) => {
        const l = parseFloat(row.length) || 0;
        const w = parseFloat(row.width) || 0;
        const h = parseFloat(row.height) || 0;
        const qty = parseFloat(row.qty) || 0;
        return acc + (l * w * h * qty);
      }, 0);
      return (sum / 1000000).toFixed(3); // Convert cm³ to m³
    }
    return formData.totalVolume;
  }, [formData.generalPackages, formData.totalVolume, formData.volumeInputType]);

  // Cargo subsection completion indicators
  const cargoStatuses = useMemo(() => {
    const hasBasics = Boolean(
      (formData.commodity && formData.commodity.trim()) ||
      (formData.packagingType && formData.packagingType.trim()) ||
      (formData.numberOfPackages && formData.numberOfPackages.trim()) ||
      (formData.totalWeight && formData.totalWeight.trim()) ||
      (formData.totalVolume && formData.totalVolume.trim()) ||
      (formData.generalPackages && formData.generalPackages.length > 0)
    );
    const hasGeneralItems = Array.isArray(formData.generalItems) && formData.generalItems.length > 0;
    const hasCosmeticsItems = Array.isArray(formData.cosmeticsItems) && formData.cosmeticsItems.length > 0;
    const hasVehicles = Array.isArray(formData.vehicles) && formData.vehicles.length > 0;
    const hasMachinery = Array.isArray(formData.machineryItems) && formData.machineryItems.length > 0;
    const hasConstruction = Array.isArray(formData.constructionItems) && formData.constructionItems.length > 0;
    const hasProject = (Array.isArray(formData.projectPieceRows) && formData.projectPieceRows.length > 0) || !!formData.projectOversize;
    const hasDangerous = (Array.isArray(formData.dangerousItems) && formData.dangerousItems.length > 0) ||
      !!formData.dangerousAnyBatteries || !!formData.dangerousAnyLiquids || !!formData.dangerousAnyPressurized;
    const hasOther = !!(formData.otherCategoryDescription && formData.otherCategoryDescription.trim());
    return { hasBasics, hasGeneralItems, hasCosmeticsItems, hasVehicles, hasMachinery, hasConstruction, hasProject, hasDangerous, hasOther };
  }, [formData]);

  // Refs for cargo detail anchors
  const basicsRef = React.useRef<HTMLDivElement | null>(null);
  const itemsRef = React.useRef<HTMLDivElement | null>(null);
  const vehiclesRef = React.useRef<HTMLDivElement | null>(null);
  const machineryRef = React.useRef<HTMLDivElement | null>(null);
  const constructionRef = React.useRef<HTMLDivElement | null>(null);
  const projectRef = React.useRef<HTMLDivElement | null>(null);
  const dangerousRef = React.useRef<HTMLDivElement | null>(null);
  const otherRef = React.useRef<HTMLDivElement | null>(null);

  const scrollToRef = (ref: React.RefObject<HTMLElement>) => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateField = <K extends keyof QuoteFormData>(field: K, value: QuoteFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Keep accurate-mode route fields in sync with lanes[0]
  const updateRouteField = (field: keyof Lane, value: any) => {
    setFormData(prev => {
      const lanes = [...(prev.lanes || [])];
      const base: Lane = lanes[0] || {
        originCountry: '', originCity: '', originType: 'door',
        destinationCountry: '', destinationCity: '', destinationType: 'door'
      };
      const lane0 = { ...base, [field]: value } as Lane;
      if (lanes.length === 0) {
        lanes.push(lane0);
      } else {
        lanes[0] = lane0;
      }
      return { ...(prev as any), [field]: value, lanes } as QuoteFormData;
    });
  };

  const emptyItem = (): SimpleItem => ({
    commodity: '',
    packagingType: '',
    numberOfPackages: '',
    totalWeight: '',
    volumeInputType: 'dimensions',
    generalPackages: [{ length: '', width: '', height: '', qty: '' }],
    totalVolume: '',
    stackable: true,
    fragile: false,
  });

  // Generic multi-item helpers for general/cosmetics
  const addItem = (kind: 'generalItems' | 'cosmeticsItems', fromSingle?: boolean) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      if (fromSingle) {
        const single: SimpleItem = {
          commodity: prev.commodity,
          packagingType: prev.packagingType,
          numberOfPackages: prev.numberOfPackages,
          totalWeight: prev.totalWeight,
          volumeInputType: prev.volumeInputType,
          generalPackages: [...prev.generalPackages],
          totalVolume: prev.totalVolume,
          stackable: prev.stackable,
          fragile: prev.fragile,
        };
        list.push(single);
      } else {
        list.push(emptyItem());
      }
      const next: QuoteFormData = { ...(prev as any) };
      (next as any)[kind] = list;
      if (fromSingle) {
        next.commodity = '';
        next.packagingType = '';
        next.numberOfPackages = '';
        next.totalWeight = '';
        next.volumeInputType = 'dimensions';
        next.generalPackages = [];
        next.totalVolume = '';
        next.stackable = true;
        next.fragile = false;
      }
      return next;
    });
  };

  const removeItem = (kind: 'generalItems' | 'cosmeticsItems', index: number) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      list.splice(index, 1);
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const updateItem = (kind: 'generalItems' | 'cosmeticsItems', index: number, field: keyof SimpleItem, value: any) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      const item = { ...list[index], [field]: value } as SimpleItem;
      list[index] = item;
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const copyItem = (kind: 'generalItems' | 'cosmeticsItems', index: number) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      const clone = JSON.parse(JSON.stringify(list[index])) as SimpleItem;
      list.splice(index + 1, 0, clone);
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const updateItemPackageRow = (
    kind: 'generalItems' | 'cosmeticsItems',
    itemIndex: number,
    rowIndex: number,
    field: 'length' | 'width' | 'height' | 'qty',
    value: string
  ) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      const item = { ...list[itemIndex] } as SimpleItem;
      const rows = [...(item.generalPackages || [])];
      rows[rowIndex] = { ...rows[rowIndex], [field]: value } as any;
      item.generalPackages = rows;
      list[itemIndex] = item;
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const addItemPackageRow = (kind: 'generalItems' | 'cosmeticsItems', itemIndex: number) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      const item = { ...list[itemIndex] } as SimpleItem;
      item.generalPackages = [...(item.generalPackages || []), { length: '', width: '', height: '', qty: '' }];
      list[itemIndex] = item;
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const removeItemPackageRow = (kind: 'generalItems' | 'cosmeticsItems', itemIndex: number, rowIndex: number) => {
    setFormData(prev => {
      const list = [...(prev[kind] as SimpleItem[])];
      const item = { ...list[itemIndex] } as SimpleItem;
      const rows = [...(item.generalPackages || [])];
      rows.splice(rowIndex, 1);
      item.generalPackages = rows;
      list[itemIndex] = item;
      const next = { ...(prev as any) };
      next[kind] = list;
      return next;
    });
  };

  const calcItemVolume = (item: SimpleItem): string => {
    if (item.volumeInputType === 'dimensions') {
      const sum = (item.generalPackages || []).reduce((acc, row) => {
        const l = parseFloat(row.length) || 0;
        const w = parseFloat(row.width) || 0;
        const h = parseFloat(row.height) || 0;
        const qty = parseFloat(row.qty) || 0;
        return acc + (l * w * h * qty);
      }, 0);
      return (sum / 1000000).toFixed(3);
    }
    return item.totalVolume || '';
  };

  // Commodity helper: nudge DG if perfumes/aerosols detected (esp. for cosmetics profile)
  const setCommodityWithDGNudge = (value: string) => {
    setFormData(prev => {
      const next = { ...prev, commodity: value } as QuoteFormData;
      const lower = value.toLowerCase();
      const isPerfume = /\b(perfume|fragrance|parfum|cologne)\b/i.test(value);
      const isAerosol = /\b(aerosol|spray|deodorant)\b/i.test(value);

      if (prev.businessProfile === 'cosmetics' && (isPerfume || isAerosol)) {
        // Nudge only: keep category as-is (usually 'general') and surface banner questions
        next.hasDangerousGoods = true;
        if (isPerfume && (next.dangerousAnyLiquids === '' || next.dangerousAnyLiquids === 'no')) {
          next.dangerousAnyLiquids = 'not-sure' as any;
        }
        if (isAerosol && (next.dangerousAnyPressurized === '' || next.dangerousAnyPressurized === 'no')) {
          next.dangerousAnyPressurized = 'not-sure' as any;
        }
      }
      return next;
    });
  };

  // Apply defaults safely when profile is chosen
  const applyProfile = (profile: BusinessProfile) => {
    const cfg = PROFILE_CONFIG[profile];
    setFormData(prev => {
      const next = { ...prev, businessProfile: profile } as QuoteFormData;

      // Strongly follow profile for these keys
      const forceKeys: (keyof QuoteFormData)[] = ['cargoCategory', 'freightMode', 'packagingType'];
      for (const k of forceKeys) {
        const v = (cfg.defaults as any)[k];
        if (v !== undefined) (next as any)[k] = v;
      }

      // Fill other defaults if empty
      for (const [k, v] of Object.entries(cfg.defaults)) {
        const key = k as keyof QuoteFormData;
        const prevVal = (next as any)[key];
        const isEmpty = prevVal === '' || prevVal === null || prevVal === undefined;
        if (isEmpty && v !== undefined) {
          (next as any)[key] = v;
        }
      }

      if (profile !== 'other') next.businessProfileOther = '';
      return next;
    });
  };

  // General package rows helpers
  const updateGeneralPackageRow = (index: number, field: 'length' | 'width' | 'height' | 'qty', value: string) => {
    setFormData(prev => {
      const rows = [...(prev.generalPackages || [])];
      rows[index] = { ...rows[index], [field]: value } as any;
      return { ...prev, generalPackages: rows };
    });
  };
  const addGeneralPackageRow = () => {
    setFormData(prev => ({ ...prev, generalPackages: [...(prev.generalPackages || []), { length: '', width: '', height: '', qty: '' }] }));
  };
  const removeGeneralPackageRow = (index: number) => {
    setFormData(prev => {
      const rows = [...(prev.generalPackages || [])];
      rows.splice(index, 1);
      return { ...prev, generalPackages: rows };
    });
  };

  // Equipment piece rows helpers (removed; handled per machinery item)

  // Project piece rows helpers
  const updateProjectPieceRow = (index: number, field: 'length' | 'width' | 'height' | 'weight', value: string) => {
    setFormData(prev => {
      const rows = [...(prev.projectPieceRows || [])];
      rows[index] = { ...rows[index], [field]: value } as any;
      return { ...prev, projectPieceRows: rows };
    });
  };
  const addProjectPieceRow = () => {
    setFormData(prev => ({ ...prev, projectPieceRows: [...(prev.projectPieceRows || []), { length: '', width: '', height: '', weight: '' }] }));
  };
  const removeProjectPieceRow = (index: number) => {
    setFormData(prev => {
      const rows = [...(prev.projectPieceRows || [])];
      rows.splice(index, 1);
      return { ...prev, projectPieceRows: rows };
    });
  };

  // Dangerous items helpers
  const addDangerousItem = (type: DangerousItemType) => {
    setFormData(prev => ({ ...prev, dangerousItems: [...(prev.dangerousItems||[]), { type, quantity: '1', note: '', customType: '' }] }));
  };
  const removeDangerousItem = (index: number) => {
    setFormData(prev => { const list=[...(prev.dangerousItems||[])]; list.splice(index,1); return { ...prev, dangerousItems: list }; });
  };
  const updateDangerousItem = (index: number, field: keyof DangerousItem, value: any) => {
    setFormData(prev => { const list=[...(prev.dangerousItems||[])]; (list[index] as any)[field]=value; return { ...prev, dangerousItems: list }; });
  };

  // Vehicles list helpers
  const addVehicle = () => {
    setFormData(prev => ({
      ...prev,
      vehicles: [...prev.vehicles, { vehicleType: 'car', vehicleMake: '', vehicleModel: '', vehicleYear: '', vehicleQuantity: '1', vehicleRunningCondition: '', vehicleRoRoOrContainer: '', vehicleDimsLength: '', vehicleDimsWidth: '', vehicleDimsHeight: '', vehicleWeightKg: '' }]
    }));
  };
  const removeVehicle = (index: number) => {
    setFormData(prev => {
      const list = [...prev.vehicles];
      list.splice(index, 1);
      return { ...prev, vehicles: list };
    });
  };
  const updateVehicle = (index: number, field: keyof VehicleEntry, value: any) => {
    setFormData(prev => {
      const list = [...prev.vehicles];
      list[index] = { ...list[index], [field]: value } as VehicleEntry;
      return { ...prev, vehicles: list };
    });
  };

  // Machinery list helpers
  const addMachineryItem = () => {
    setFormData(prev => ({
      ...prev,
      machineryItems: [...prev.machineryItems, { equipmentType: '', equipmentQuantity: '1', equipmentCondition: '', equipmentPieces: '', equipmentPieceRows: [{ length:'', width:'', height:'', weight:'' }], equipmentRequiresForklift: false, equipmentRequiresCrane: false, equipmentCanBeDismantled: false }]
    }));
  };
  const removeMachineryItem = (index: number) => {
    setFormData(prev => {
      const list = [...prev.machineryItems];
      list.splice(index,1);
      return { ...prev, machineryItems: list };
    });
  };
  const updateMachineryItem = (index: number, field: keyof MachineryItem, value: any) => {
    setFormData(prev => {
      const list = [...prev.machineryItems];
      (list[index] as any)[field] = value;
      return { ...prev, machineryItems: list };
    });
  };
  const updateMachineryPieceRow = (itemIndex: number, rowIndex: number, field: 'length'|'width'|'height'|'weight', value: string) => {
    setFormData(prev => {
      const list = [...prev.machineryItems];
      const rows = [...list[itemIndex].equipmentPieceRows];
      rows[rowIndex] = { ...rows[rowIndex], [field]: value } as any;
      list[itemIndex] = { ...list[itemIndex], equipmentPieceRows: rows };
      return { ...prev, machineryItems: list };
    });
  };
  const addMachineryPieceRow = (itemIndex: number) => {
    setFormData(prev => {
      const list = [...prev.machineryItems];
      list[itemIndex] = { ...list[itemIndex], equipmentPieceRows: [...list[itemIndex].equipmentPieceRows, { length:'', width:'', height:'', weight:'' }] };
      return { ...prev, machineryItems: list };
    });
  };
  const removeMachineryPieceRow = (itemIndex: number, rowIndex: number) => {
    setFormData(prev => {
      const list = [...prev.machineryItems];
      const rows = [...list[itemIndex].equipmentPieceRows];
      rows.splice(rowIndex,1);
      list[itemIndex] = { ...list[itemIndex], equipmentPieceRows: rows };
      return { ...prev, machineryItems: list };
    });
  };

  // Construction list helpers
  const addConstructionItem = () => {
    setFormData(prev => ({
      ...prev,
      constructionItems: [...prev.constructionItems, { constructionMaterialType: '', constructionPackaging: '', constructionTotalWeight: '', constructionVolumeInputType: 'dimensions', constructionPackages: [{length:'',width:'',height:'',qty:''}], constructionTotalVolume: '', constructionLongestItemLength: '', constructionWeatherSensitive: false }]
    }));
  };
  const removeConstructionItem = (index: number) => {
    setFormData(prev => {
      const list = [...prev.constructionItems];
      list.splice(index,1);
      return { ...prev, constructionItems: list };
    });
  };
  const updateConstructionItem = (index: number, field: keyof ConstructionItem, value: any) => {
    setFormData(prev => {
      const list = [...prev.constructionItems];
      (list[index] as any)[field] = value;
      return { ...prev, constructionItems: list };
    });
  };

  const nextStep = () => { if (step < totalSteps) setStep(step + 1); };

  const prevStep = () => { if (step > 1) setStep(step - 1); };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('kind', 'quote');
      formDataToSend.append('name', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      if (formData.company) formDataToSend.append('company', formData.company);
      if (formData.notes) formDataToSend.append('notes', formData.notes);
      
      // Add all form fields for detailed quote information
      formDataToSend.append('quoteIntent', formData.quoteIntent);
      formDataToSend.append('businessProfile', formData.businessProfile);
      formDataToSend.append('shipmentFrequency', formData.shipmentFrequency);
      
      // Route information
      formDataToSend.append('originCountry', formData.originCountry);
      formDataToSend.append('originCity', formData.originCity);
      formDataToSend.append('originType', formData.originType);
      formDataToSend.append('destinationCountry', formData.destinationCountry);
      formDataToSend.append('destinationCity', formData.destinationCity);
      formDataToSend.append('destinationType', formData.destinationType);
      
      // Freight mode
      formDataToSend.append('freightMode', formData.freightMode);
      if (formData.seaType) formDataToSend.append('seaType', formData.seaType);
      if (formData.containerSize) formDataToSend.append('containerSize', formData.containerSize);
      if (formData.truckType) formDataToSend.append('truckType', formData.truckType);
      
      // Cargo details
      formDataToSend.append('cargoCategory', formData.cargoCategory);
      if (formData.commodity) formDataToSend.append('commodity', formData.commodity);
      if (formData.packagingType) formDataToSend.append('packagingType', formData.packagingType);
      if (formData.numberOfPackages) formDataToSend.append('numberOfPackages', formData.numberOfPackages);
      if (formData.totalWeight) formDataToSend.append('totalWeight', formData.totalWeight);
      if (formData.totalVolume) formDataToSend.append('totalVolume', formData.totalVolume);
      if (formData.cargoValue) formDataToSend.append('cargoValue', formData.cargoValue);
      
      // Timing
      if (formData.cargoReadyDate) formDataToSend.append('cargoReadyDate', formData.cargoReadyDate);
      formDataToSend.append('priority', formData.priority);
      if (formData.targetDeliveryDate) formDataToSend.append('targetDeliveryDate', formData.targetDeliveryDate);
      
      // Include lanes for price-check mode
      if (formData.lanes && formData.lanes.length > 0) {
        formDataToSend.append('lanes', JSON.stringify(formData.lanes));
      }
      if (formData.shippingFrequency) formDataToSend.append('shippingFrequency', formData.shippingFrequency);
      if (formData.typicalWeightBucket) formDataToSend.append('typicalWeightBucket', formData.typicalWeightBucket);
      if (formData.typicalVolumeBucket) formDataToSend.append('typicalVolumeBucket', formData.typicalVolumeBucket);
      if (formData.typicalContainer) formDataToSend.append('typicalContainer', formData.typicalContainer);
      if (formData.timeSensitivity) formDataToSend.append('timeSensitivity', formData.timeSensitivity);
      if (formData.deadlineDate) formDataToSend.append('deadlineDate', formData.deadlineDate);
      
      // Include cargo items
      if (formData.generalItems && formData.generalItems.length > 0) {
        formDataToSend.append('generalItems', JSON.stringify(formData.generalItems));
      }
      if (formData.cosmeticsItems && formData.cosmeticsItems.length > 0) {
        formDataToSend.append('cosmeticsItems', JSON.stringify(formData.cosmeticsItems));
      }
      if (formData.vehicles && formData.vehicles.length > 0) {
        formDataToSend.append('vehicles', JSON.stringify(formData.vehicles));
      }
      if (formData.machineryItems && formData.machineryItems.length > 0) {
        formDataToSend.append('machineryItems', JSON.stringify(formData.machineryItems));
      }
      if (formData.constructionItems && formData.constructionItems.length > 0) {
        formDataToSend.append('constructionItems', JSON.stringify(formData.constructionItems));
      }
      if (formData.dangerousItems && formData.dangerousItems.length > 0) {
        formDataToSend.append('dangerousItems', JSON.stringify(formData.dangerousItems));
      }
      
      // Service scope
      formDataToSend.append('pickupNeeded', formData.pickupNeeded);
      formDataToSend.append('deliveryNeeded', formData.deliveryNeeded);
      formDataToSend.append('customsClearanceNeeded', formData.customsClearanceNeeded);
      
      const response = await fetch('/api/enquiry', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        // The function will redirect to /thanks
        window.location.href = '/thanks';
      } else {
        const errorText = await response.text();
        alert(lang === 'ar' ? 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.' : 'Error submitting form. Please try again.');
        console.error('Submit error:', errorText);
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert(lang === 'ar' ? 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.' : 'Error submitting form. Please try again.');
    }
  };

  const stepTitles = {
    1: lang === 'ar' ? 'من أنت؟' : 'About You',
    2: lang === 'ar' ? 'المسار' : 'Route',
    3: lang === 'ar' ? 'طريقة الشحن' : 'Freight Mode',
    4: lang === 'ar' ? 'تفاصيل البضاعة' : 'Cargo Details',
    5: lang === 'ar' ? 'التوقيت' : 'Timing',
    6: lang === 'ar' ? 'معلومات الاتصال' : 'Contact',
  } as const;

  return (
    <div className="fixed inset-0 z-[60] bg-luxury-paper overflow-y-auto animate-fade-in" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-50 p-2 bg-white/10 backdrop-blur-md border border-stone-200 rounded-full text-luxury-black hover:bg-luxury-black hover:text-white transition-all duration-300 group shadow-lg"
      >
        <X size={24} strokeWidth={1} className="group-hover:rotate-90 transition-transform duration-300" />
      </button>

      <div className="min-h-screen flex flex-col lg:flex-row">
        
        {/* Left Side - Visual */}
        <div className="lg:w-1/3 bg-luxury-black text-white p-12 lg:p-16 flex flex-col justify-between relative overflow-hidden">
           <div className="relative z-10">
              <span className="text-greenline-400 text-xs uppercase tracking-[0.3em] font-bold mb-4 block">
                 {lang === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
              </span>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-6">
                 {lang === 'ar' ? 'نحسب المسافات،' : 'Calculated precision for'} <br />
                 <span className="italic text-white/70">{lang === 'ar' ? 'لنختصر الزمن.' : 'global movement.'}</span>
              </h2>
           </div>

           <div className="relative z-10 hidden lg:block">
              <div className="space-y-6">
                 <div className="flex items-center gap-4 text-white/60">
                    <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center">
                       <Ship size={20} strokeWidth={1} />
                    </div>
                    <span className="text-xs uppercase tracking-widest">Ocean Freight</span>
                 </div>
                 <div className="flex items-center gap-4 text-white/60">
                    <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center">
                       <Plane size={20} strokeWidth={1} />
                    </div>
                    <span className="text-xs uppercase tracking-widest">Air Cargo</span>
                 </div>
                 <div className="flex items-center gap-4 text-white/60">
                    <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center">
                       <Truck size={20} strokeWidth={1} />
                    </div>
                    <span className="text-xs uppercase tracking-widest">Inland Transport</span>
                 </div>
              </div>
           </div>
        </div>

        {/* Right Side - Form */}
        <div className="lg:w-2/3 p-6 md:p-16 lg:p-24 bg-white">
           <div className="max-w-2xl mx-auto">
              {/* Progress Bar */}
              <div className="mb-12 border-b border-stone-100 pb-8">
                 <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-widest text-greenline-900">Step {step} of {totalSteps}</span>
                    <span className="text-sm font-serif text-luxury-black/70">{
                      ((intent: QuoteIntent, s: number) => {
                        if (intent === 'price-check') {
                          const pc = { 1: (lang==='ar'?'من أنت؟':'About You'), 2: (lang==='ar'?'المسارات':'Compare Lanes'), 3: (lang==='ar'?'الملف والشحنة':'Profile & Size'), 4: (lang==='ar'?'معلومات الاتصال':'Contact') } as const;
                          return pc[s as 1|2|3|4];
                        } else {
                          const acc = { 1: (lang==='ar'?'من أنت؟':'About You'), 2: (lang==='ar'?'المسار':'Route'), 3: (lang==='ar'?'طريقة الشحن':'Freight Mode'), 4: (lang==='ar'?'تفاصيل البضاعة':'Cargo Details'), 5: (lang==='ar'?'التوقيت':'Timing'), 6: (lang==='ar'?'معلومات الاتصال':'Contact') } as const;
                          return acc[s as 1|2|3|4|5|6];
                        }
                      })(formData.quoteIntent, step)
                    }</span>
                 </div>
                 <div className="flex gap-2">
                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
                      <div 
                        key={s} 
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          s <= step ? 'bg-greenline-900' : 'bg-stone-200'
                        }`}
                      />
                    ))}
                 </div>
              </div>

              <form className="space-y-8 animate-fade-up" onSubmit={(e) => e.preventDefault()}>
                {/* Step 1 (new) - About You */}
                {step === 1 && (
                  <div className="space-y-6">
                    {/* Goal selection */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                        {lang === 'ar' ? 'ما هو هدفك؟' : 'What is your goal?'}
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[{v:'accurate', en:'Get an accurate quote', ar:'عرض سعر دقيق'}, {v:'price-check', en:'Price check / planning', ar:'تحقق من الأسعار / التخطيط'}].map(opt => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => updateField('quoteIntent', opt.v as QuoteIntent)}
                            className={`border p-4 text-left transition-all ${
                              formData.quoteIntent === (opt.v as QuoteIntent)
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            <div className="font-medium">{lang==='ar'?opt.ar:opt.en}</div>
                            {opt.v==='price-check' && (
                              <div className="text-xs opacity-70 mt-1">{lang==='ar'?'للمهتمين بالمقارنة بدون تفاصيل دقيقة':'For planning without exact dates/sizes'}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-luxury-black">
                        {lang === 'ar' ? 'من أنت؟' : 'What best describes you?'}
                      </h3>
                      <p className="text-sm text-stone-500">
                        {lang === 'ar'
                          ? 'سنقوم بتخصيص نموذج عرض السعر حسب نشاطك.'
                          : 'We’ll tailor the quote form based on your business.'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(Object.keys(PROFILE_CONFIG) as BusinessProfile[]).map((p) => {
                        const cfg = PROFILE_CONFIG[p];
                        const active = formData.businessProfile === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => applyProfile(p)}
                            className={`border p-4 text-left transition-all ${
                              active
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            <div className="font-medium">{cfg.label[lang]}</div>
                            {cfg.hint && <div className="text-xs opacity-70 mt-1">{cfg.hint[lang]}</div>}
                          </button>
                        );
                      })}
                    </div>

                    {formData.businessProfile === 'other' && (
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                          {lang === 'ar' ? 'صف نشاطك' : 'Describe your business'}
                        </label>
                        <input
                          type="text"
                          className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent"
                          value={formData.businessProfileOther}
                          onChange={(e) => updateField('businessProfileOther', e.target.value)}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                        {lang === 'ar' ? 'كم مرة تشحن عادة؟' : 'How often do you ship?'}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { v: 'one-off', en: 'One-time', ar: 'مرة واحدة' },
                          { v: 'regular', en: 'Regular', ar: 'متكرر' },
                          { v: '', en: 'Not sure', ar: 'غير متأكد' },
                        ].map(opt => (
                          <button
                            key={opt.en}
                            type="button"
                            onClick={() => updateField('shipmentFrequency', opt.v as any)}
                            className={`border p-3 text-xs transition-all text-center ${
                              formData.shipmentFrequency === (opt.v as any)
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            {lang === 'ar' ? opt.ar : opt.en}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 - Route / Lanes */}
                {step === 2 && (
                  <>
                    {formData.quoteIntent === 'price-check' ? (
                      <div className="space-y-6">
                        <p className="text-sm text-stone-600 leading-relaxed">
                          {lang==='ar'
                            ? 'أضف مسارات الشحن التي تريد مقارنتها (حتى 5). لكل مسار، حدِّد دولة ومدينة المنشأ والوجهة واختر نوع النقاط (باب، مطار، ميناء، سكة).'
                            : 'Add the shipping lanes you want to compare (up to 5). For each lane, set origin/destination country and city, and choose point types (door, airport, seaport, rail).'}
                        </p>
                        <div className="flex items-center justify-between">
                          <h3 className="font-serif text-lg text-luxury-black">{lang==='ar'?'أضف المسارات (1–5)':'Add Lanes (1–5)'}</h3>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              lanes: prev.lanes.length < 5 ? [...prev.lanes, { originCountry:'', originCity:'', originType:'door', destinationCountry:'', destinationCity:'', destinationType:'door' }] : prev.lanes
                            }))}
                            className={`text-sm ${formData.lanes.length>=5?'text-stone-400 cursor-not-allowed':'text-greenline-900 hover:underline'}`}
                            disabled={formData.lanes.length>=5}
                          >{lang==='ar'?'إضافة مسار':'Add lane'}</button>
                        </div>

                        {(formData.lanes||[]).map((lane, idx) => (
                          <div key={idx} className="p-4 border border-stone-200 rounded-lg space-y-6 bg-stone-50">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-stone-700">{lang==='ar'?'مسار':'Lane'} #{idx+1}</div>
                              {formData.lanes.length>1 && (
                                <button type="button" className="text-stone-500 hover:text-red-600 text-sm" onClick={() => setFormData(prev=>{ const list=[...prev.lanes]; list.splice(idx,1); return { ...prev, lanes: list.length?list:[{originCountry:'',originCity:'',originType:'door',destinationCountry:'',destinationCity:'',destinationType:'door'}] }; })}>{lang==='ar'?'حذف':'Remove'}</button>
                              )}
                            </div>

                            {/* Origin */}
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <MapPin size={18} className="text-greenline-900" />
                                <h4 className="font-serif text-md text-luxury-black">{lang==='ar'?'المنشأ':'Origin'}</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'بلد المنشأ':'Origin Country'}</label>
                                  <CountrySelect
                                    value={lane.originCountry}
                                    onChange={(val)=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], originCountry:val}; return { ...prev, lanes:l}; })}
                                    options={COUNTRIES}
                                    placeholder={lang==='ar'? 'ابحث عن الدولة' : 'Search country'}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'مدينة المنشأ':'Origin City'}</label>
                                  <input className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={lane.originCity} onChange={(e)=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], originCity:e.target.value}; return { ...prev, lanes:l}; })} placeholder={lang==='ar'?'مثال: شنغهاي':'e.g. Shanghai'} />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  { value: 'door', label: lang === 'ar' ? 'الباب / العنوان' : 'Door / Address' },
                                  { value: 'airport', label: lang === 'ar' ? 'المطار' : 'Airport' },
                                  { value: 'seaport', label: lang === 'ar' ? 'الميناء' : 'Seaport' },
                                  { value: 'rail', label: lang === 'ar' ? 'محطة السكك' : 'Rail Terminal' },
                                ].map((type) => (
                                  <button key={type.value} type="button" onClick={()=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], originType:type.value as LocationType}; return { ...prev, lanes:l}; })} className={`border p-3 text-xs transition-all text-center ${lane.originType===type.value? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50 text-stone-600'}`}>{type.label}</button>
                                ))}
                              </div>
                            </div>

                            {/* Destination */}
                            <div className="space-y-3 pt-2 border-t border-stone-200">
                              <div className="flex items-center gap-2">
                                <MapPin size={18} className="text-greenline-900" />
                                <h4 className="font-serif text-md text-luxury-black">{lang==='ar'?'الوجهة':'Destination'}</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'بلد الوجهة':'Destination Country'}</label>
                                  <CountrySelect
                                    value={lane.destinationCountry}
                                    onChange={(val)=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], destinationCountry:val}; return { ...prev, lanes:l}; })}
                                    options={COUNTRIES}
                                    placeholder={lang==='ar'? 'ابحث عن الدولة' : 'Search country'}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'مدينة الوجهة':'Destination City'}</label>
                                  <input className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={lane.destinationCity} onChange={(e)=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], destinationCity:e.target.value}; return { ...prev, lanes:l}; })} placeholder={lang==='ar'?'مثال: بنغازي':'e.g. Benghazi'} />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  { value: 'door', label: lang === 'ar' ? 'الباب / العنوان' : 'Door / Address' },
                                  { value: 'airport', label: lang === 'ar' ? 'المطار' : 'Airport' },
                                  { value: 'seaport', label: lang === 'ar' ? 'الميناء' : 'Seaport' },
                                  { value: 'rail', label: lang === 'ar' ? 'محطة السكك' : 'Rail Terminal' },
                                ].map((type) => (
                                  <button key={type.value} type="button" onClick={()=>setFormData(prev=>{ const l=[...prev.lanes]; l[idx]={...l[idx], destinationType:type.value as LocationType}; return { ...prev, lanes:l}; })} className={`border p-3 text-xs transition-all text-center ${lane.destinationType===type.value? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50 text-stone-600'}`}>{type.label}</button>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <div>
                      <p className="mb-6 text-sm text-stone-600 leading-relaxed">
                        {lang==='ar'
                          ? 'حدِّد دولة ومدينة المنشأ والوجهة. اختر نوع النقطة (باب، مطار، ميناء، سكة) للاستلام والتسليم. سنستخدم ذلك لاقتراح المسارات والخدمات المناسبة.'
                          : 'Provide origin and destination country and city. Select the point type (door, airport, seaport, rail) for pickup and delivery. We’ll use this to suggest suitable routings and services.'}
                      </p>
                       {/* Origin */}
                       <div className="space-y-6">
                          <div className="flex items-center gap-2 mb-4">
                             <MapPin size={18} className="text-greenline-900" />
                             <h3 className="font-serif text-lg text-luxury-black">{lang === 'ar' ? 'المنشأ' : 'Origin'}</h3>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'بلد المنشأ' : 'Origin Country'}
                                </label>
                                <CountrySelect
                                  value={formData.originCountry}
                                  onChange={(val) => updateRouteField('originCountry', val)}
                                  options={COUNTRIES}
                                  placeholder={lang === 'ar' ? 'ابحث عن الدولة' : 'Search country'}
                                />
                             </div>
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'مدينة المنشأ' : 'Origin City'}
                                </label>
                                <input 
                                  type="text" 
                                  className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                                  placeholder={lang === 'ar' ? 'مثال: شنغهاي' : 'e.g. Shanghai'}
                                  value={formData.originCity}
                                  onChange={(e) => updateRouteField('originCity', e.target.value)}
                                />
                             </div>
                          </div>

                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'نوع المنشأ' : 'Origin Type'}
                             </label>
                             <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  { value: 'door', label: lang === 'ar' ? 'الباب / العنوان' : 'Door / Address' },
                                  { value: 'airport', label: lang === 'ar' ? 'المطار' : 'Airport' },
                                  { value: 'seaport', label: lang === 'ar' ? 'الميناء' : 'Seaport' },
                                  { value: 'rail', label: lang === 'ar' ? 'محطة السكك' : 'Rail Terminal' },
                                ].map((type) => (
                                  <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => updateRouteField('originType', type.value as LocationType)}
                                    className={`border p-3 text-xs transition-all text-center ${
                                      formData.originType === type.value
                                        ? 'border-greenline-900 bg-greenline-900 text-white'
                                        : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50 text-stone-600'
                                    }`}
                                  >
                                     {type.label}
                                  </button>
                                ))}
                             </div>
                          </div>
                       </div>

                       {/* Destination */}
                       <div className="space-y-6 pt-6 border-t border-stone-100">
                          <div className="flex items-center gap-2 mb-4">
                             <MapPin size={18} className="text-greenline-900" />
                             <h3 className="font-serif text-lg text-luxury-black">{lang === 'ar' ? 'الوجهة' : 'Destination'}</h3>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'بلد الوجهة' : 'Destination Country'}
                                </label>
                                <CountrySelect
                                  value={formData.destinationCountry}
                                  onChange={(val) => updateRouteField('destinationCountry', val)}
                                  options={COUNTRIES}
                                  placeholder={lang === 'ar' ? 'ابحث عن الدولة' : 'Search country'}
                                />
                             </div>
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'مدينة الوجهة' : 'Destination City'}
                                </label>
                                <input 
                                  type="text" 
                                  className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                                  placeholder={lang === 'ar' ? 'مثال: بنغازي' : 'e.g. Benghazi'}
                                  value={formData.destinationCity}
                                  onChange={(e) => updateRouteField('destinationCity', e.target.value)}
                                />
                             </div>
                          </div>

                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'نوع الوجهة' : 'Destination Type'}
                             </label>
                             <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                  { value: 'door', label: lang === 'ar' ? 'الباب / العنوان' : 'Door / Address' },
                                  { value: 'airport', label: lang === 'ar' ? 'المطار' : 'Airport' },
                                  { value: 'seaport', label: lang === 'ar' ? 'الميناء' : 'Seaport' },
                                  { value: 'rail', label: lang === 'ar' ? 'محطة السكك' : 'Rail Terminal' },
                                ].map((type) => (
                                  <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => updateRouteField('destinationType', type.value as LocationType)}
                                    className={`border p-3 text-xs transition-all text-center ${
                                      formData.destinationType === type.value
                                        ? 'border-greenline-900 bg-greenline-900 text-white'
                                        : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50 text-stone-600'
                                    }`}
                                  >
                                     {type.label}
                                  </button>
                                ))}
                             </div>
                          </div>
                       </div>

                       {/* Delivery Scope */}
                       <div className="bg-stone-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                             <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'نطاق التسليم' : 'Delivery Scope'}
                             </span>
                             <span className="text-sm font-serif text-greenline-900 font-medium">{formData.deliveryScope}</span>
                          </div>
                       </div>

                       {/* Preferred Route removed from UI to avoid TS mismatch */}
                    </div>
                    )}
                  </>
                )}

                {/* Step 3 - Freight Mode or Planning Profile */}
                {step === 3 && (
                  <>
                    {formData.quoteIntent === 'price-check' ? (
                      <div className="space-y-8">
                        {/* Frequency */}
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'تكرار الشحن':'Shipping frequency'}</label>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                              {v:'one-off', en:'One-off', ar:'مرة واحدة'},
                              {v:'weekly', en:'Weekly', ar:'أسبوعي'},
                              {v:'twice-monthly', en:'Twice per month', ar:'مرتان شهرياً'},
                              {v:'monthly', en:'Monthly', ar:'شهري'},
                              {v:'ad-hoc', en:'Ad-hoc', ar:'أحياناً'},
                            ].map(opt => (
                              <button key={opt.v} type="button" onClick={()=>updateField('shippingFrequency', opt.v as Frequency)} className={`border p-3 text-xs transition-all text-center ${formData.shippingFrequency===opt.v? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?opt.ar:opt.en}</button>
                            ))}
                          </div>
                        </div>

                        {/* Optional counts */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الشحنات شهرياً (اختياري)':'Typical shipments per month (optional)'}</label>
                            <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={formData.shipmentsPerMonth} onChange={(e)=>updateField('shipmentsPerMonth', e.target.value)} />
                          </div>
                        </div>

                        {/* Typical size buckets */}
                        <div className="space-y-4">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'حجم الشحنة المعتاد':'Typical shipment size'}</label>
                          <div className="space-y-3">
                            <div>
                              <div className="text-xs text-stone-600 mb-2">{lang==='ar'?'نطاق الوزن (كجم)':'Weight range (kg)'}</div>
                              <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                                {(['0-5','5-20','20-50','50-100','100-300','300-1000','1000+'] as WeightBucket[]).map(b => (
                                  <button key={b} type="button" onClick={()=>updateField('typicalWeightBucket', b)} className={`border p-2 text-xs ${formData.typicalWeightBucket===b?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{b}</button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-stone-600 mb-2">{lang==='ar'?'نطاق الحجم (CBM)':'Volume range (CBM)'}</div>
                              <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                                {(['<0.1','0.1-0.5','0.5-1','1-3','3-8','8-15','15+'] as VolumeBucket[]).map(b => (
                                  <button key={b} type="button" onClick={()=>updateField('typicalVolumeBucket', b)} className={`border p-2 text-xs ${formData.typicalVolumeBucket===b?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{b}</button>
                                ))}
                              </div>
                            </div>
                            {/* Optional container for sea */}
                            <div>
                              <div className="text-xs text-stone-600 mb-2">{lang==='ar'?'حاوية مبدئية (بحري)':'Typical container (sea, optional)'}</div>
                              <div className="grid grid-cols-3 gap-2">
                                {(['','20ft','40ft','40hc'] as (''|ContainerSize)[]).map(v => (
                                  <button key={v||'none'} type="button" onClick={()=>updateField('typicalContainer', v)} className={`border p-2 text-xs ${formData.typicalContainer===v?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{v===''?(lang==='ar'?'بدون':'None'):(v)}</button>
                                ))}
                              </div>
                            </div>

                            {/* Quick presets */}
                            <div className="pt-2">
                              <div className="text-xs text-stone-600 mb-2">{lang==='ar'?'سريعاً: اختيارات جاهزة':'Quick: common presets'}</div>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                {[{
                                  key:'few-cartons',
                                  labelEn:'Few cartons', labelAr:'بضع كراتين', apply: () => setFormData(prev => ({...prev, typicalWeightBucket:'0-5', typicalVolumeBucket:'<0.1', typicalContainer:''}))
                                },{
                                  key:'one-pallet',
                                  labelEn:'1 pallet', labelAr:'بالتة واحدة', apply: () => setFormData(prev => ({...prev, typicalWeightBucket:'100-300', typicalVolumeBucket:'0.5-1', typicalContainer:''}))
                                },{
                                  key:'two-four-pallets',
                                  labelEn:'2–4 pallets', labelAr:'2–4 بالتات', apply: () => setFormData(prev => ({...prev, typicalWeightBucket:'300-1000', typicalVolumeBucket:'1-3', typicalContainer:''}))
                                },{
                                  key:'half-container',
                                  labelEn:'Half container', labelAr:'نصف حاوية', apply: () => setFormData(prev => ({...prev, typicalWeightBucket:'300-1000', typicalVolumeBucket:'8-15', typicalContainer:''}))
                                },{
                                  key:'full-container',
                                  labelEn:"Full container (20'/40')", labelAr:"حاوية كاملة (20/40)", apply: () => setFormData(prev => ({...prev, typicalWeightBucket:'1000+', typicalVolumeBucket:'15+', typicalContainer: prev.typicalContainer || '20ft'}))
                                }].map(p => (
                                  <button key={p.key} type="button" onClick={p.apply} className="border p-2 text-xs border-stone-200 hover:border-greenline-900 hover:bg-stone-50">
                                    {lang==='ar'?p.labelAr:p.labelEn}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* What are you shipping? */}
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'ما الذي تشحنه؟':'What are you shipping?'}</label>
                          <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={formData.cargoCategory} onChange={(e)=>updateField('cargoCategory', e.target.value as any)}>
                            {(ALL_CARGO_CATEGORIES).map(c => (<option key={c} value={c}>{c}</option>))}
                          </select>
                        </div>

                        {/* DG simplified */}
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'أي بطاريات / سوائل / عطور؟':'Any batteries / liquids / perfumes?'}</label>
                          <div className="grid grid-cols-3 gap-3">
                            {[{v:'yes',en:'Yes',ar:'نعم'},{v:'no',en:'No',ar:'لا'},{v:'not-sure',en:'Not sure',ar:'غير متأكد'}].map(opt => (
                              <button key={opt.v} type="button" onClick={()=>setFormData(prev=>({ ...prev, dangerousAnyBatteries: opt.v as any, dangerousAnyLiquids: opt.v as any, dangerousAnyPressurized: opt.v as any}))} className={`border p-3 text-xs text-center ${formData.dangerousAnyBatteries===opt.v && formData.dangerousAnyLiquids===opt.v && formData.dangerousAnyPressurized===opt.v? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?opt.ar:opt.en}</button>
                            ))}
                          </div>
                        </div>

                        {/* Time sensitivity */}
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'حساسية الوقت':'Time sensitivity'}</label>
                          <div className="grid grid-cols-3 gap-3">
                            {[{v:'flexible',en:'Flexible',ar:'مرن'},{v:'fast',en:'Need it fast',ar:'أحتاجها بسرعة'},{v:'deadline',en:'Fixed deadline',ar:'موعد نهائي'}].map(opt => (
                              <button key={opt.v} type="button" onClick={()=>updateField('timeSensitivity', opt.v as any)} className={`border p-3 text-xs text-center ${formData.timeSensitivity===opt.v? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?opt.ar:opt.en}</button>
                            ))}
                          </div>
                        </div>
                        {formData.timeSensitivity==='deadline' && (
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'تاريخ الموعد النهائي':'Deadline date'}</label>
                            <input type="date" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={formData.deadlineDate} onChange={(e)=>updateField('deadlineDate', e.target.value)} />
                          </div>
                        )}
                      </div>
                    ) : (
                    <div>
                       <p className="mb-6 text-sm text-stone-600 leading-relaxed">
                         {lang === 'ar'
                           ? 'اختر طريقة الشحن الرئيسية لهذه الشحنة. في الشحن البحري، حدِّد LCL أو FCL وحجم الحاوية إذا كان مناسباً. أشِر إلى أي بضائع خطرة أو متطلبات خاصة عند الحاجة. إذا لم تكن متأكداً، اختر "غير متأكد" وسنوصي بالأنسب.'
                           : 'Select the primary mode for this shipment. For sea, choose LCL or FCL and container size where applicable. Indicate any dangerous goods or special handling as needed. If unsure, select “Not Sure” and we will advise.'}
                       </p>
                       <div className="space-y-4">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'طريقة الشحن' : 'Freight Mode'}
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                             {[
                               { value: 'sea', icon: Ship, label: lang === 'ar' ? 'بحري' : 'Sea' },
                               { value: 'air', icon: Plane, label: lang === 'ar' ? 'جوي' : 'Air' },
                               { value: 'road', icon: Truck, label: lang === 'ar' ? 'بري' : 'Road' },
                               { value: 'rail', icon: Train, label: lang === 'ar' ? 'سكة حديد' : 'Rail' },
                               { value: 'not-sure', icon: Package, label: lang === 'ar' ? 'غير متأكد' : 'Not Sure' },
                             ].map((mode) => {
                               const Icon = mode.icon;
                               return (
                                 <button
                                   key={mode.value}
                                   type="button"
                                   onClick={() => updateField('freightMode', mode.value as FreightMode)}
                                   className={`border p-4 transition-all text-left group ${
                                     formData.freightMode === mode.value
                                       ? 'border-greenline-900 bg-greenline-900 text-white'
                                       : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                                   }`}
                                 >
                                    <div className="flex items-center gap-3">
                                      <Icon size={20} strokeWidth={1.5} />
                                      <span className="font-medium">{mode.label}</span>
                                    </div>
                                 </button>
                               );
                             })}
                          </div>
                       </div>

                       {/* Sea Options */}
                       {formData.freightMode === 'sea' && (
                          <div className="space-y-4 pt-4 border-t border-stone-100 animate-fade-up">
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'نوع الشحن البحري' : 'Sea Freight Type'}
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                   {[
                                     { value: 'lcl', label: lang === 'ar' ? 'مشترك (LCL)' : 'LCL (Shared)' },
                                     { value: 'fcl', label: lang === 'ar' ? 'حاوية كاملة (FCL)' : 'FCL (Full Container)' },
                                   ].map((type) => (
                                     <button
                                       key={type.value}
                                       type="button"
                                       onClick={() => updateField('seaType', type.value as 'lcl' | 'fcl')}
                                       className={`border p-4 transition-all text-center ${
                                         formData.seaType === type.value
                                           ? 'border-greenline-900 bg-greenline-900 text-white'
                                           : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                                       }`}
                                     >
                                        {type.label}
                                     </button>
                                   ))}
                                </div>
                             </div>

                             {formData.seaType === 'fcl' && (
                                <div className="space-y-2 animate-fade-up">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                     {lang === 'ar' ? 'حجم الحاوية' : 'Container Size'}
                                   </label>
                                   <div className="grid grid-cols-3 gap-3">
                                      {[
                                        { value: '20ft', label: "20'" },
                                        { value: '40ft', label: "40'" },
                                        { value: '40hc', label: "40'HC" },
                                      ].map((size) => (
                                        <button
                                          key={size.value}
                                          type="button"
                                          onClick={() => updateField('containerSize', size.value as ContainerSize)}
                                          className={`border p-3 transition-all text-center ${
                                            formData.containerSize === size.value
                                              ? 'border-greenline-900 bg-greenline-900 text-white'
                                              : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                                          }`}
                                        >
                                           {size.label}
                                        </button>
                                      ))}
                                   </div>
                                </div>
                             )}
                          </div>
                       )}

                       {/* Air Options */}
                       {formData.freightMode === 'air' && (
                          <div className="space-y-4 pt-4 border-t border-stone-100 animate-fade-up">
                             <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                                <span className="text-sm text-stone-700">
                                  {lang === 'ar' ? 'هل تحتوي البضاعة على بطاريات أو مواد خطرة؟' : 'Any batteries / dangerous goods?'}
                                </span>
                                <div className="flex gap-2">
                                   <button
                                     type="button"
                                     onClick={() => updateField('hasDangerousGoods', true)}
                                     className={`px-4 py-2 text-sm transition-all ${
                                       formData.hasDangerousGoods
                                         ? 'bg-greenline-900 text-white'
                                         : 'bg-white border border-stone-200 hover:border-greenline-900'
                                     }`}
                                   >
                                      {lang === 'ar' ? 'نعم' : 'Yes'}
                                   </button>
                                   <button
                                     type="button"
                                     onClick={() => updateField('hasDangerousGoods', false)}
                                     className={`px-4 py-2 text-sm transition-all ${
                                       !formData.hasDangerousGoods
                                         ? 'bg-greenline-900 text-white'
                                         : 'bg-white border border-stone-200 hover:border-greenline-900'
                                     }`}
                                   >
                                      {lang === 'ar' ? 'لا' : 'No'}
                                   </button>
                                </div>
                             </div>
                          </div>
                       )}

                       {/* Road Options */}
                       {formData.freightMode === 'road' && (
                          <div className="space-y-4 pt-4 border-t border-stone-100 animate-fade-up">
                             <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                  {lang === 'ar' ? 'نوع الشاحنة المطلوب' : 'Truck Type (Optional)'}
                                </label>
                                <select 
                                  className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent appearance-none cursor-pointer"
                                  value={formData.truckType}
                                  onChange={(e) => updateField('truckType', e.target.value)}
                                >
                                   <option value="">{lang === 'ar' ? 'اختر النوع' : 'Select Type'}</option>
                                   {TRUCK_TYPES.map((type) => (
                                     <option key={type} value={type}>{type}</option>
                                   ))}
                                </select>
                             </div>

                             <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                                <span className="text-sm text-stone-700">
                                  {lang === 'ar' ? 'هل تتوفر رافعة شوكية للتحميل/التفريغ؟' : 'Forklift available for loading/unloading?'}
                                </span>
                                <div className="flex gap-2">
                                   <button
                                     type="button"
                                     onClick={() => updateField('forkliftAvailable', true)}
                                     className={`px-4 py-2 text-sm transition-all ${
                                       formData.forkliftAvailable
                                         ? 'bg-greenline-900 text-white'
                                         : 'bg-white border border-stone-200 hover:border-greenline-900'
                                     }`}
                                   >
                                      {lang === 'ar' ? 'نعم' : 'Yes'}
                                   </button>
                                   <button
                                     type="button"
                                     onClick={() => updateField('forkliftAvailable', false)}
                                     className={`px-4 py-2 text-sm transition-all ${
                                       !formData.forkliftAvailable
                                         ? 'bg-greenline-900 text-white'
                                         : 'bg-white border border-stone-200 hover:border-greenline-900'
                                     }`}
                                   >
                                      {lang === 'ar' ? 'لا' : 'No'}
                                   </button>
                                </div>
                             </div>
                          </div>
                       )}
                    </div>
                    )}
                  </>
                 )}

                {/* Step 4 - Cargo Details or Contact (planning) */}
                {step === 4 && (
                  <>
                    {formData.quoteIntent === 'price-check' ? (
                      <div className="space-y-6">
                        {/* Contact (reused) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'} *</label>
                            <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder={lang === 'ar' ? 'اسمك الكامل' : 'Your Full Name'} value={formData.fullName} onChange={(e) => updateField('fullName', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang === 'ar' ? 'الشركة (اختياري)' : 'Company (Optional)'}</label>
                            <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder={lang === 'ar' ? 'اسم الشركة' : 'Company Name'} value={formData.company} onChange={(e) => updateField('company', e.target.value)} />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang === 'ar' ? 'رقم الهاتف / واتساب' : 'Phone / WhatsApp'} *</label>
                            <input type="tel" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="+218 ..." value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (Optional)'}</label>
                            <input type="email" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="email@company.com" value={formData.email} onChange={(e) => updateField('email', e.target.value)} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang === 'ar' ? 'ملاحظات إضافية' : 'Additional Notes'}</label>
                          <textarea rows={4} className="w-full border border-stone-200 p-4 text-sm focus:outline-none focus:border-greenline-900 transition-colors bg-transparent resize-none" placeholder={lang === 'ar' ? 'أي متطلبات خاصة...' : 'Any special requirements...'} value={formData.notes} onChange={(e) => updateField('notes', e.target.value)} />
                        </div>
                      </div>
                    ) : (
                    <div>
                      <p className="mb-3 text-sm text-stone-600 leading-relaxed">
                        {lang === 'ar'
                          ? 'زوِّد تفاصيل الشحنة للحصول على تسعير دقيق: اختر فئة الشحنة، وحدِّد السلعة ونوع التغليف، وعدد الطرود والوزن، ثم أدخل الأبعاد أو الحجم الكلي. أشِر إلى إمكانية الترصيص (Stackable) وقابلية الكسر (Fragile) وأي بضائع خطرة أو متطلبات خاصة. بالنسبة للمركبات/الآلات/مواد البناء، أكمِل القسم المخصص.'
                          : 'Provide cargo details for accurate pricing: select the cargo category, specify the commodity and packaging, enter package counts and total weight, then add dimensions or total volume. Note stackability and fragility, and indicate any dangerous goods or special handling. For vehicles, machinery, or construction materials, complete the dedicated section.'}
                      </p>
                      {/* Section status chips */}
                      <div className="mb-6 flex flex-wrap gap-2">
                        {[
                          { key: 'items', label: lang==='ar' ? (formData.cargoCategory==='cosmetics'?'عناصر التجميل':'عناصر عامة') : (formData.cargoCategory==='cosmetics'?'Cosmetics Items':'General Items'), filled: formData.cargoCategory==='cosmetics' ? cargoStatuses.hasCosmeticsItems : cargoStatuses.hasGeneralItems },
                          { key: 'vehicles', label: lang==='ar'?'مركبات':'Vehicles', filled: cargoStatuses.hasVehicles },
                          { key: 'machinery', label: lang==='ar'?'معدات/آلات':'Machinery', filled: cargoStatuses.hasMachinery },
                          { key: 'construction', label: lang==='ar'?'مواد بناء':'Construction', filled: cargoStatuses.hasConstruction },
                          { key: 'project', label: lang==='ar'?'مشروع':'Project', filled: cargoStatuses.hasProject },
                          { key: 'dangerous', label: lang==='ar'?'مواد خطرة':'Dangerous', filled: cargoStatuses.hasDangerous },
                          { key: 'other', label: lang==='ar'?'أخرى':'Other', filled: cargoStatuses.hasOther },
                        ].map(s => {
                          const active = (
                            s.key === 'items' ? (formData.cargoCategory === 'general' || formData.cargoCategory === 'cosmetics') :
                            s.key === 'vehicles' ? formData.cargoCategory === 'vehicles' :
                            s.key === 'machinery' ? formData.cargoCategory === 'machinery' :
                            s.key === 'construction' ? formData.cargoCategory === 'construction' :
                            s.key === 'project' ? formData.cargoCategory === 'project' :
                            s.key === 'dangerous' ? formData.cargoCategory === 'dangerous' :
                            s.key === 'other' ? formData.cargoCategory === 'other' : false
                          );
                          return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => {
                              const decideCategory = (key: string): CargoCategory | null => {
                                switch (key) {
                                  case 'basics':
                                  case 'items': {
                                    // Prefer whichever item set exists; fallback to current if general/cosmetics; else 'general'
                                    if (cargoStatuses.hasCosmeticsItems) return 'cosmetics';
                                    if (cargoStatuses.hasGeneralItems) return 'general';
                                    if (formData.cargoCategory === 'cosmetics' || formData.cargoCategory === 'general') return formData.cargoCategory;
                                    return 'general';
                                  }
                                  case 'vehicles': return 'vehicles';
                                  case 'machinery': return 'machinery';
                                  case 'construction': return 'construction';
                                  case 'project': return 'project';
                                  case 'dangerous': return 'dangerous';
                                  case 'other': return 'other';
                                  default: return null;
                                }
                              };
                              const target = decideCategory(s.key);
                              if (target) updateField('cargoCategory', target);
                            }}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border ${
                              active
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : s.filled
                                  ? 'border-greenline-900 text-greenline-900 bg-greenline-900/5'
                                  : 'border-stone-200 text-stone-500 hover:border-greenline-900'
                            }`}
                          >
                            {s.filled ? (
                              <Check size={14} />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-stone-300" />
                            )}
                            {s.label}
                          </button>
                          );
                        })}
                      </div>
                       {/* Cargo Category */}
                       <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'فئة البضاعة' : 'Cargo Category'}
                          </label>
                          <select
                            className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent appearance-none cursor-pointer"
                            value={formData.cargoCategory}
                            onChange={(e) => updateField('cargoCategory', e.target.value as CargoCategory)}
                          >
                            {(() => {
                              const allowed = formData.businessProfile
                                ? PROFILE_CONFIG[formData.businessProfile].allowedCargoCategories
                                : ALL_CARGO_CATEGORIES.filter(c => c !== 'cosmetics');
                              return (
                                <>
                                  {allowed.includes('general') && (
                                    <option value="general">{lang === 'ar' ? 'بضاعة عامة (كراتين/بالتات)' : 'General cargo (cartons/pallets)'}</option>
                                  )}
                                  {allowed.includes('cosmetics') && (
                                    <option value="cosmetics">{lang === 'ar' ? 'مستحضرات تجميل / عناية' : 'Cosmetics / beauty'}</option>
                                  )}
                                  {allowed.includes('vehicles') && (
                                    <option value="vehicles">{lang === 'ar' ? 'مركبات (سيارات/دراجات نارية)' : 'Vehicles (cars/motorbikes)'}</option>
                                  )}
                                  {allowed.includes('machinery') && (
                                    <option value="machinery">{lang === 'ar' ? 'معدات/آلات' : 'Machinery / equipment'}</option>
                                  )}
                                  {allowed.includes('construction') && (
                                    <option value="construction">{lang === 'ar' ? 'مواد بناء (حديد/أنابيب/أسمنت)' : 'Construction materials (steel, pipes, cement, tiles, timber)'}</option>
                                  )}
                                  {allowed.includes('project') && (
                                    <option value="project">{lang === 'ar' ? 'بضاعة مشروع (أحجام كبيرة/أحمال ثقيلة)' : 'Project cargo (oversize/heavy lift)'}</option>
                                  )}
                                  {allowed.includes('dangerous') && (
                                    <option value="dangerous">{lang === 'ar' ? 'مواد خطرة / بطاريات' : 'Dangerous goods / batteries'}</option>
                                  )}
                                  {allowed.includes('other') && (
                                    <option value="other">{lang === 'ar' ? 'أخرى' : 'Other'}</option>
                                  )}
                                </>
                              );
                            })()}
                          </select>
                       </div>

                       {/* Other category description */}
                       {formData.cargoCategory === 'other' && (
                          <div ref={otherRef} className="space-y-2 animate-fade-up">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'وصف البضاعة' : 'Cargo Description'}
                             </label>
                             <input
                               type="text"
                               className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                               placeholder={lang === 'ar' ? 'مثال: أثاث، مواد طبية...' : 'e.g. Furniture, medical supplies...'}
                               value={formData.otherCategoryDescription}
                               onChange={(e) => updateField('otherCategoryDescription', e.target.value)}
                             />
                          </div>
                       )}

                       {/* General/cosmetics cargo fields */}
                       {(formData.cargoCategory === 'general' || formData.cargoCategory === 'cosmetics') && (
                          <>
                            <div ref={basicsRef} />
                            {formData.cargoCategory === 'cosmetics' && (
                              <div ref={dangerousRef} className="mt-1 p-4 rounded-lg border border-stone-200 bg-stone-50 text-stone-800 space-y-4">
                                <div className="text-sm font-medium">
                                  {lang === 'ar'
                                    ? 'قد تُعتبر بعض منتجات التجميل بضائع خطرة (مثل العطور/البخاخات). أسئلة سريعة للتأكيد:'
                                    : 'Cosmetics may be DG (e.g., perfumes/aerosols). A few quick questions to confirm:'}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div className="space-y-2">
                                    <div className="text-xs font-semibold">{lang === 'ar' ? 'سوائل؟' : 'Any liquids?'}</div>
                                    <div className="flex gap-2">
                                      {(['no','not-sure','yes'] as const).map(v => (
                                        <button key={v} type="button" onClick={() => updateField('dangerousAnyLiquids', v as any)}
                                          className={`border p-2 text-xs transition-all ${formData.dangerousAnyLiquids === v ? 'border-greenline-900 bg-greenline-900 text-white' : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}
                                        >{lang==='ar'?(v==='yes'?'نعم':v==='no'?'لا':'غير متأكد'):(v==='yes'?'Yes':v==='no'?'No':'Not sure')}</button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="text-xs font-semibold">{lang === 'ar' ? 'مضغوط/بخاخ؟' : 'Pressurized/aerosol?'}</div>
                                    <div className="flex gap-2">
                                      {(['no','not-sure','yes'] as const).map(v => (
                                        <button key={v} type="button" onClick={() => updateField('dangerousAnyPressurized', v as any)}
                                          className={`border p-2 text-xs transition-all ${formData.dangerousAnyPressurized === v ? 'border-greenline-900 bg-greenline-900 text-white' : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}
                                        >{lang==='ar'?(v==='yes'?'نعم':v==='no'?'لا':'غير متأكد'):(v==='yes'?'Yes':v==='no'?'No':'Not sure')}</button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="text-xs font-semibold">{lang === 'ar' ? 'SDS متوفرة؟' : 'SDS available?'}</div>
                                    <div className="flex gap-2">
                                      <button type="button" onClick={() => updateField('dangerousSDSAvailable', true)} className={`border p-2 text-xs transition-all ${formData.dangerousSDSAvailable ? 'border-greenline-900 bg-greenline-900 text-white' : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'نعم':'Yes'}</button>
                                      <button type="button" onClick={() => updateField('dangerousSDSAvailable', false)} className={`border p-2 text-xs transition-all ${!formData.dangerousSDSAvailable ? 'border-greenline-900 bg-greenline-900 text-white' : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'لا':'No'}</button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                       

                       {(formData.cargoCategory === 'general' || formData.cargoCategory === 'cosmetics') && (
                         <>
                           {((formData.cargoCategory === 'cosmetics' ? formData.cosmeticsItems.length : formData.generalItems.length) > 0) ? (
                             <>
                               {/* Multi-items list */}
                               <div ref={itemsRef} />
                               {(
                                 formData.cargoCategory === 'cosmetics' ? formData.cosmeticsItems : formData.generalItems
                               ).map((it, idx) => (
                                 <div key={idx} className="p-4 border border-stone-200 rounded-lg bg-stone-50 space-y-4">
                                   <div className="flex items-center justify-between">
                                     <h4 className="text-sm font-medium text-stone-700">{lang==='ar'?`عنصر #${idx+1}`:`Item #${idx+1}`}</h4>
                                     <div className="flex gap-3">
                                       <button type="button" className="text-stone-500 hover:text-luxury-black text-xs underline" onClick={() => copyItem(formData.cargoCategory === 'cosmetics' ? 'cosmeticsItems' : 'generalItems', idx)}>
                                         {lang==='ar'?'نسخ':'Duplicate'}
                                       </button>
                                       <button type="button" className="text-stone-500 hover:text-red-600 text-xs underline" onClick={() => removeItem(formData.cargoCategory === 'cosmetics' ? 'cosmeticsItems' : 'generalItems', idx)}>
                                         {lang==='ar'?'إزالة':'Remove'}
                                       </button>
                                     </div>
                                   </div>

                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div className="space-y-2">
                                       <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?(formData.cargoCategory==='cosmetics'?'نوع مستحضرات التجميل':'السلعة'):(formData.cargoCategory==='cosmetics'?'Cosmetic Type':'Commodity')}</label>
                                       {formData.cargoCategory==='cosmetics' ? (
                                         <>
                                           {(() => {
                                             const isPreset = COSMETIC_TYPES.includes(it.commodity);
                                             const current = isPreset ? it.commodity : 'other';
                                             return (
                                               <>
                                                 <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer"
                                                   value={current}
                                                   onChange={(e)=>{
                                                     const val = e.target.value;
                                                     if (val === 'other') {
                                                       // Switch to custom entry by clearing the commodity so the text input appears
                                                       updateItem('cosmeticsItems', idx, 'commodity', '');
                                                     } else {
                                                       updateItem('cosmeticsItems', idx, 'commodity', val);
                                                     }
                                                   }}
                                                 >
                                                   {COSMETIC_TYPES.map(t => (<option key={t} value={t}>{t}</option>))}
                                                   <option value="other">{lang==='ar'?'أخرى':'Other'}</option>
                                                 </select>
                                                 {!isPreset && (
                                                   <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'اذكر النوع':'Specify type'} value={it.commodity} onChange={(e)=>updateItem('cosmeticsItems', idx, 'commodity', e.target.value)} />
                                                 )}
                                               </>
                                             );
                                           })()}
                                         </>
                                       ) : (
                                         <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={it.commodity} onChange={(e)=>updateItem('generalItems', idx, 'commodity', e.target.value)} />
                                       )}
                                     </div>
                                     <div className="space-y-2">
                                       <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'نوع التغليف':'Packaging Type'}</label>
                                       <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={it.packagingType} onChange={(e)=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'packagingType', e.target.value)}>
                                         <option value="">{lang==='ar'?'اختر النوع':'Select Type'}</option>
                                         {PACKAGING_TYPES.map(type => (<option key={type} value={type}>{type}</option>))}
                                       </select>
                                     </div>
                                   </div>

                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                     <div className="space-y-2">
                                       <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'عدد الطرود':'Number of Packages'}</label>
                                       <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={it.numberOfPackages} onChange={(e)=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'numberOfPackages', e.target.value)} />
                                     </div>
                                     <div className="space-y-2">
                                       <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الوزن الكلي (كجم)':'Total Weight (kg)'}</label>
                                       <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={it.totalWeight} onChange={(e)=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'totalWeight', e.target.value)} />
                                     </div>
                                   </div>

                                   <div className="space-y-4">
                                     <div className="flex gap-4">
                                       <button type="button" onClick={()=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'volumeInputType', 'dimensions')} className={`flex-1 border p-3 text-sm transition-all ${it.volumeInputType==='dimensions'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الأبعاد':'I know dimensions'}</button>
                                       <button type="button" onClick={()=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'volumeInputType', 'total')} className={`flex-1 border p-3 text-sm transition-all ${it.volumeInputType==='total'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الحجم الكلي':'I know total volume'}</button>
                                     </div>

                                     {it.volumeInputType==='dimensions' ? (
                                       <div className="space-y-4">
                                         {(it.generalPackages||[]).map((row, rIdx) => (
                                           <div key={rIdx} className="grid grid-cols-12 gap-3 items-end">
                                             <div className="col-span-3 space-y-2"><label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">L (cm)</label><input type="number" className="w-full border-b border-stone-200 py-3 bg-transparent" value={row.length} onChange={(e)=>updateItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, rIdx, 'length', e.target.value)} /></div>
                                             <div className="col-span-3 space-y-2"><label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">W (cm)</label><input type="number" className="w-full border-b border-stone-200 py-3 bg-transparent" value={row.width} onChange={(e)=>updateItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, rIdx, 'width', e.target.value)} /></div>
                                             <div className="col-span-3 space-y-2"><label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">H (cm)</label><input type="number" className="w-full border-b border-stone-200 py-3 bg-transparent" value={row.height} onChange={(e)=>updateItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, rIdx, 'height', e.target.value)} /></div>
                                             <div className="col-span-2 space-y-2"><label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Qty</label><input type="number" className="w-full border-b border-stone-200 py-3 bg-transparent" value={row.qty} onChange={(e)=>updateItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, rIdx, 'qty', e.target.value)} /></div>
                                             <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>removeItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, rIdx)} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                           </div>
                                         ))}
                                         <div className="flex justify-between items-center">
                                           <button type="button" onClick={()=>addItemPackageRow(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx)} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة صف':'Add row'}</button>
                                           <div className="text-sm text-stone-500">{lang==='ar'?'الحجم المحسوب:':'Calculated Volume:'} <span className="font-medium text-greenline-900">{calcItemVolume(it)} CBM</span></div>
                                         </div>
                                       </div>
                                     ) : (
                                       <div className="space-y-2">
                                         <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الحجم الكلي (CBM)':'Total Volume (CBM)'}</label>
                                         <input type="number" step="0.001" className="w-full border-b border-stone-200 py-3 bg-transparent" value={it.totalVolume} onChange={(e)=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'totalVolume', e.target.value)} />
                                       </div>
                                     )}

                                     <div className="grid grid-cols-2 gap-4 pt-2">
                                       <div className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg">
                                         <button type="button" onClick={()=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'stackable', !it.stackable)} className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${it.stackable?'bg-greenline-900 border-greenline-900':'border-stone-300'}`}>{it.stackable && <Check size={14} className="text-white" />}</button>
                                         <span className="text-xs text-stone-700">{lang==='ar'?'قابل للتكديس':'Stackable'}</span>
                                       </div>
                                       <div className="flex items-center gap-3 p-3 bg-stone-100 rounded-lg">
                                         <button type="button" onClick={()=>updateItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems', idx, 'fragile', !it.fragile)} className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${it.fragile?'bg-greenline-900 border-greenline-900':'border-stone-300'}`}>{it.fragile && <Check size={14} className="text-white" />}</button>
                                         <span className="text-xs text-stone-700">{lang==='ar'?'قابل للكسر / يُرجى الحذر':'Fragile / handle with care'}</span>
                                       </div>
                                     </div>
                                   </div>
                                 </div>
                               ))}

                               <div className="flex justify-between items-center pt-3">
                                 <button type="button" onClick={()=>addItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems')} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة عنصر':'Add item'}</button>
                                 {(() => {
                                   const items = formData.cargoCategory==='cosmetics'?formData.cosmeticsItems:formData.generalItems;
                                   const totalWeight = items.reduce((acc, it)=> acc + (parseFloat(it.totalWeight||'0')||0), 0);
                                   const totalPackages = items.reduce((acc, it)=> acc + (parseFloat(it.numberOfPackages||'0')||0), 0);
                                   const totalCbm = items.reduce((acc, it)=> acc + (parseFloat(calcItemVolume(it)||'0')||0), 0);
                                   return (
                                     <div className="text-xs text-stone-600">
                                       {lang==='ar'? 'الإجمالي: ' : 'Totals: '}<span className="font-medium text-luxury-black">{totalPackages} pkgs</span> · <span className="font-medium text-luxury-black">{totalWeight.toFixed(2)} kg</span> · <span className="font-medium text-luxury-black">{totalCbm.toFixed(3)} CBM</span>
                                     </div>
                                   );
                                 })()}
                               </div>
                             </>
                           ) : (
                             <>
                               {/* Empty state for first item */}
                               <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                                 <div className="mb-2 text-sm">{lang==='ar'?'أضف أول عنصر للبضاعة':'Add your first cargo item'}</div>
                                 <button type="button" onClick={()=>addItem(formData.cargoCategory==='cosmetics'?'cosmeticsItems':'generalItems')} className="text-xs px-4 py-2 border border-stone-300 rounded hover:border-greenline-900 hover:text-greenline-900 transition">{lang==='ar'?'إضافة عنصر':'Add item'}</button>
                               </div>
                             </>
                           )}
                         </>
                       )}

                       {/* Vehicles-specific fields (multiple) */}
                      {formData.cargoCategory === 'vehicles' && (
                        <div ref={vehiclesRef} className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                          {(formData.vehicles || []).length === 0 ? (
                            <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                              <div className="mb-2 text-sm">{lang==='ar'?'أضف أول مركبة':'Add your first vehicle'}</div>
                              <button type="button" onClick={addVehicle} className="text-xs px-4 py-2 border border-stone-300 rounded hover:border-greenline-900 hover:text-greenline-900 transition">{lang==='ar'?'إضافة مركبة':'Add vehicle'}</button>
                            </div>
                          ) : (
                            <>
                          {(formData.vehicles || []).map((v, idx) => (
                             <div key={idx} className="space-y-4 p-4 border border-stone-200 rounded-lg bg-stone-50">
                               <div className="flex items-center justify-between">
                                 <h4 className="text-sm font-medium text-stone-700">Vehicle #{idx + 1}</h4>
                                 <button type="button" className="text-stone-500 hover:text-red-600 text-sm" onClick={() => removeVehicle(idx)}>Remove</button>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'نوع المركبة':'Vehicle Type'}</label>
                                   <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={v.vehicleType} onChange={(e)=>updateVehicle(idx,'vehicleType', e.target.value as VehicleType)}>
                                     <option value="car">{lang==='ar'?'سيارة':'Car'}</option>
                                     <option value="4x4">{lang==='ar'?'سيارة دفع رباعي':'4x4 / SUV'}</option>
                                     <option value="van">{lang==='ar'?'شاحنة صغيرة':'Van'}</option>
                                     <option value="truck">{lang==='ar'?'شاحنة':'Truck'}</option>
                                     <option value="motorbike">{lang==='ar'?'دراجة نارية':'Motorbike'}</option>
                                   </select>
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الماركة / الموديل / السنة':'Make / Model / Year'}</label>
                                   <div className="grid grid-cols-3 gap-3">
                                     <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'الماركة':'Make'} value={v.vehicleMake} onChange={(e)=>updateVehicle(idx,'vehicleMake', e.target.value)} />
                                     <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'الموديل':'Model'} value={v.vehicleModel} onChange={(e)=>updateVehicle(idx,'vehicleModel', e.target.value)} />
                                     <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'السنة':'Year'} value={v.vehicleYear} onChange={(e)=>updateVehicle(idx,'vehicleYear', e.target.value)} />
                                   </div>
                                 </div>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الكمية':'Quantity'}</label>
                                   <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder="1" value={v.vehicleQuantity} onChange={(e)=>updateVehicle(idx,'vehicleQuantity', e.target.value)} />
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'حالة التشغيل':'Running Condition'}</label>
                                   <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={v.vehicleRunningCondition} onChange={(e)=>updateVehicle(idx,'vehicleRunningCondition', e.target.value as any)}>
                                     <option value="">{lang==='ar'?'اختر':'Select'}</option>
                                     <option value="running">{lang==='ar'?'تعمل':'Running'}</option>
                                     <option value="non-running">{lang==='ar'?'لا تعمل':'Non-running'}</option>
                                   </select>
                                 </div>
                               </div>
                               <div className="space-y-2">
                                 <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'أبعاد المركبة (اختياري)':'Vehicle dimensions (optional)'}</label>
                                 <div className="grid grid-cols-4 gap-3">
                                   <input type="number" placeholder="L (cm)" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={v.vehicleDimsLength||''} onChange={(e)=>updateVehicle(idx,'vehicleDimsLength', e.target.value)} />
                                   <input type="number" placeholder="W (cm)" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={v.vehicleDimsWidth||''} onChange={(e)=>updateVehicle(idx,'vehicleDimsWidth', e.target.value)} />
                                   <input type="number" placeholder="H (cm)" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={v.vehicleDimsHeight||''} onChange={(e)=>updateVehicle(idx,'vehicleDimsHeight', e.target.value)} />
                                   <input type="number" placeholder={lang==='ar'?'الوزن (كجم)':'Weight (kg)'} className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={v.vehicleWeightKg||''} onChange={(e)=>updateVehicle(idx,'vehicleWeightKg', e.target.value)} />
                                 </div>
                               </div>
                               {formData.freightMode === 'sea' && (
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'طريقة الشحن البحري':'Sea Freight Method'}</label>
                                   <div className="grid grid-cols-3 gap-3">
                                     {['roro','container','not-sure'].map(method => (
                                       <button key={method} type="button" onClick={()=>updateVehicle(idx,'vehicleRoRoOrContainer', method as any)} className={`border p-3 text-xs transition-all text-center ${v.vehicleRoRoOrContainer===method? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{method==='roro'?'RoRo':method==='container'?'Container':(lang==='ar'?'غير متأكد':'Not sure')}</button>
                                     ))}
                                   </div>
                                 </div>
                               )}
                             </div>
                           ))}
                           <button type="button" onClick={addVehicle} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة مركبة أخرى':'Add another vehicle'}</button>
                           </>
                          )}
                         </div>
                       )}

                       {/* Machinery/Equipment fields */}
                      {formData.cargoCategory === 'machinery' && (
                        <div ref={machineryRef} className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                          {(formData.machineryItems || []).length === 0 ? (
                            <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                              <div className="mb-2 text-sm">{lang==='ar'?'أضف أول معدة':'Add your first equipment item'}</div>
                              <button type="button" onClick={addMachineryItem} className="text-xs px-4 py-2 border border-stone-300 rounded hover:border-greenline-900 hover:text-greenline-900 transition">{lang==='ar'?'إضافة عنصر':'Add item'}</button>
                            </div>
                          ) : (
                            <>
                          {(formData.machineryItems || []).map((m, mIdx) => (
                             <div key={mIdx} className="space-y-4 p-4 border border-stone-200 rounded-lg bg-stone-50">
                               <div className="flex items-center justify-between">
                                 <h4 className="text-sm font-medium text-stone-700">{lang==='ar'?'معدة':'Item'} #{mIdx+1}</h4>
                                 <button type="button" className="text-stone-500 hover:text-red-600 text-sm" onClick={()=>removeMachineryItem(mIdx)}>Remove</button>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'نوع المعدات':'Equipment Type'}</label>
                                   <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'مثال: حفار':'e.g. Excavator'} value={m.equipmentType} onChange={(e)=>updateMachineryItem(mIdx,'equipmentType', e.target.value)} />
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الكمية':'Quantity'}</label>
                                   <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder="1" value={m.equipmentQuantity} onChange={(e)=>updateMachineryItem(mIdx,'equipmentQuantity', e.target.value)} />
                                 </div>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الحالة':'Condition'}</label>
                                   <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={m.equipmentCondition} onChange={(e)=>updateMachineryItem(mIdx,'equipmentCondition', e.target.value as any)}>
                                     <option value="">{lang==='ar'?'اختر':'Select'}</option>
                                     <option value="new">{lang==='ar'?'جديدة':'New'}</option>
                                     <option value="used">{lang==='ar'?'مستعملة':'Used'}</option>
                                   </select>
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'عدد القطع':'Number of pieces'}</label>
                                   <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder="1" value={m.equipmentPieces} onChange={(e)=>updateMachineryItem(mIdx,'equipmentPieces', e.target.value)} />
                                 </div>
                               </div>
                               <div className="space-y-3">
                                 <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{lang==='ar'?'تفاصيل القطع':'Piece details'}</div>
                                 {(m.equipmentPieceRows||[]).map((row, idx) => (
                                   <div key={idx} className="grid grid-cols-12 gap-3 items-end">
                                     <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="L (cm)" value={row.length} onChange={(e)=>updateMachineryPieceRow(mIdx, idx,'length', e.target.value)} />
                                     <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="W (cm)" value={row.width} onChange={(e)=>updateMachineryPieceRow(mIdx, idx,'width', e.target.value)} />
                                     <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="H (cm)" value={row.height} onChange={(e)=>updateMachineryPieceRow(mIdx, idx,'height', e.target.value)} />
                                     <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder={lang==='ar'?'الوزن (كجم)':'Weight (kg)'} value={row.weight} onChange={(e)=>updateMachineryPieceRow(mIdx, idx,'weight', e.target.value)} />
                                     <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>removeMachineryPieceRow(mIdx, idx)} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                   </div>
                                 ))}
                                 <div>
                                   <button type="button" onClick={()=>addMachineryPieceRow(mIdx)} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة صف':'Add row'}</button>
                                 </div>
                               </div>
                               <div className="space-y-2">
                                 <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'متطلبات التحميل':'Handling needs'}</label>
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                   <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg"><span className="text-xs text-stone-700">Crane</span><button type="button" onClick={()=>updateMachineryItem(mIdx,'equipmentRequiresCrane', !m.equipmentRequiresCrane)} className={`w-10 h-5 rounded-full transition-all ${m.equipmentRequiresCrane?'bg-greenline-900':'bg-stone-300'}`}><div className={`w-4 h-4 rounded-full bg-white transition-transform ${m.equipmentRequiresCrane?'translate-x-5':'translate-x-0.5'}`} /></button></div>
                                   <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg"><span className="text-xs text-stone-700">Forklift</span><button type="button" onClick={()=>updateMachineryItem(mIdx,'equipmentRequiresForklift', !m.equipmentRequiresForklift)} className={`w-10 h-5 rounded-full transition-all ${m.equipmentRequiresForklift?'bg-greenline-900':'bg-stone-300'}`}><div className={`w-4 h-4 rounded-full bg-white transition-transform ${m.equipmentRequiresForklift?'translate-x-5':'translate-x-0.5'}`} /></button></div>
                                   <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg"><span className="text-xs text-stone-700">{lang==='ar'?'قابل للتفكيك':'Can be dismantled'}</span><button type="button" onClick={()=>updateMachineryItem(mIdx,'equipmentCanBeDismantled', !m.equipmentCanBeDismantled)} className={`w-10 h-5 rounded-full transition-all ${m.equipmentCanBeDismantled?'bg-greenline-900':'bg-stone-300'}`}><div className={`w-4 h-4 rounded-full bg-white transition-transform ${m.equipmentCanBeDismantled?'translate-x-5':'translate-x-0.5'}`} /></button></div>
                                 </div>
                               </div>
                             </div>
                            ))}
                            <button type="button" onClick={addMachineryItem} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة معدة أخرى':'Add another item'}</button>
                            </>
                          )}
                          </div>
                        )}

                      {/* Project cargo fields */}
                      {formData.cargoCategory === 'project' && (
                        <div ref={projectRef} className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                          <div className="space-y-3">
                            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{lang==='ar'?'قائمة القطع':'Piece list'}</div>
                            {(formData.projectPieceRows||[]).length === 0 ? (
                              <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                                <div className="mb-2 text-sm">{lang==='ar'?'أضف أول قطعة':'Add your first piece'}</div>
                                <button type="button" onClick={addProjectPieceRow} className="text-xs px-4 py-2 border border-stone-300 rounded hover:border-greenline-900 hover:text-greenline-900 transition">{lang==='ar'?'إضافة قطعة':'Add piece'}</button>
                              </div>
                            ) : (
                              <>
                                {(formData.projectPieceRows||[]).map((row, idx)=> (
                                  <div key={idx} className="grid grid-cols-12 gap-3 items-end">
                                    <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="L (cm)" value={row.length} onChange={(e)=>updateProjectPieceRow(idx,'length',e.target.value)} />
                                    <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="W (cm)" value={row.width} onChange={(e)=>updateProjectPieceRow(idx,'width',e.target.value)} />
                                    <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="H (cm)" value={row.height} onChange={(e)=>updateProjectPieceRow(idx,'height',e.target.value)} />
                                    <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder={lang==='ar'?'الوزن (كجم)':'Weight (kg)'} value={row.weight} onChange={(e)=>updateProjectPieceRow(idx,'weight',e.target.value)} />
                                    <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>removeProjectPieceRow(idx)} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                  </div>
                                ))}
                                <div>
                                  <button type="button" onClick={addProjectPieceRow} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة قطعة':'Add piece'}</button>
                                </div>
                              </>
                            )}
                          </div>

                           <div className="space-y-2">
                            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'المناولة المطلوبة':'Handling needed'}</label>
                             <div className="grid grid-cols-3 gap-3">
                               <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"><span className="text-xs text-stone-700">Crane</span><button type="button" onClick={()=>updateField('projectHandlingCrane', !formData.projectHandlingCrane)} className={`w-10 h-5 rounded-full transition-all ${formData.projectHandlingCrane?'bg-greenline-900':'bg-stone-300'}`}> <div className={`w-4 h-4 rounded-full bg-white transition-transform ${formData.projectHandlingCrane?'translate-x-5':'translate-x-0.5'}`} /></button></div>
                               <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"><span className="text-xs text-stone-700">Lowbed/heavy haul</span><button type="button" onClick={()=>updateField('projectHandlingLowbed', !formData.projectHandlingLowbed)} className={`w-10 h-5 rounded-full transition-all ${formData.projectHandlingLowbed?'bg-greenline-900':'bg-stone-300'}`}> <div className={`w-4 h-4 rounded-full bg-white transition-transform ${formData.projectHandlingLowbed?'translate-x-5':'translate-x-0.5'}`} /></button></div>
                               <div className="space-y-2">
                                 <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'مرافقة/تصاريح':'Escort/permits'}</label>
                                 <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent appearance-none cursor-pointer" value={formData.projectPermitsEscort} onChange={(e)=>updateField('projectPermitsEscort', e.target.value as 'yes'|'no'|'not-sure'|'')}>
                                   <option value="">{lang==='ar'?'اختر':'Select'}</option>
                                   <option value="yes">{lang==='ar'?'نعم':'Yes'}</option>
                                   <option value="no">{lang==='ar'?'لا':'No'}</option>
                                   <option value="not-sure">{lang==='ar'?'غير متأكد':'Not sure'}</option>
                                 </select>
                               </div>
                             </div>
                           </div>

                           <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'قيود موقع التسليم (اختياري)':'Delivery site constraints (optional)'}</label>
                             <textarea className="w-full border border-stone-200 p-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" rows={3} placeholder={lang==='ar'?'اكتب الملاحظات...':'Describe access, ground, overhead, etc.'} value={formData.projectSiteConstraints} onChange={(e)=>updateField('projectSiteConstraints', e.target.value)} />
                           </div>
                         </div>
                       )}

                       {/* Construction materials fields (old, hidden) */}
                       {false && formData.cargoCategory === 'construction' && (
                          <div className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                     {lang === 'ar' ? 'نوع المادة' : 'Material Type'}
                                   </label>
                                   <select
                                     className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent appearance-none cursor-pointer"
                                     value={formData.constructionMaterialType}
                                     onChange={(e) => updateField('constructionMaterialType', e.target.value as 'tiles' | 'steel' | 'pipes' | 'cement' | 'timber' | 'other' | '')}
                                   >
                                      <option value="">{lang === 'ar' ? 'اختر' : 'Select'}</option>
                                      <option value="tiles">{lang === 'ar' ? 'بلاط' : 'Tiles'}</option>
                                      <option value="steel">{lang === 'ar' ? 'فولاذ' : 'Steel'}</option>
                                      <option value="pipes">{lang === 'ar' ? 'أنابيب' : 'Pipes'}</option>
                                      <option value="cement">{lang === 'ar' ? 'أسمنت' : 'Cement'}</option>
                                      <option value="timber">{lang === 'ar' ? 'خشب' : 'Timber'}</option>
                                      <option value="other">{lang === 'ar' ? 'أخرى' : 'Other'}</option>
                                   </select>
                                </div>
                                <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                                     {lang === 'ar' ? 'التغليف' : 'Packaging'}
                                   </label>
                                   <select
                                     className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent appearance-none cursor-pointer"
                                     value={formData.constructionPackaging}
                                     onChange={(e) => updateField('constructionPackaging', e.target.value as 'loose' | 'bundles' | 'pallets' | 'big-bags' | '')}
                                   >
                                      <option value="">{lang === 'ar' ? 'اختر' : 'Select'}</option>
                                      <option value="loose">{lang === 'ar' ? 'سائب' : 'Loose'}</option>
                                      <option value="bundles">{lang === 'ar' ? 'حزم' : 'Bundles'}</option>
                                      <option value="pallets">{lang === 'ar' ? 'بالتات' : 'Pallets'}</option>
                                      <option value="big-bags">{lang === 'ar' ? 'أكياس كبيرة' : 'Big Bags'}</option>
                                   </select>
                                </div>
                             </div>

                             {/* Totals */}
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                               <div className="space-y-2">
                                 <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الوزن الكلي (كجم)':'Total Weight (kg)'}</label>
                                 <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="0" value={formData.constructionTotalWeight} onChange={(e)=>updateField('constructionTotalWeight', e.target.value)} />
                               </div>
                               <div className="space-y-2">
                                 <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'طول أقصى (م)':'Longest length (m)'}</label>
                                 <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="0" value={formData.constructionLongestItemLength} onChange={(e)=>updateField('constructionLongestItemLength', e.target.value)} />
                               </div>
                             </div>

                             {/* Volume input (same as general) */}
                             <div className="space-y-4">
                               <div className="flex gap-4">
                                 <button type="button" onClick={()=>updateField('constructionVolumeInputType', 'dimensions')} className={`flex-1 border p-3 text-sm transition-all ${formData.constructionVolumeInputType==='dimensions'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الأبعاد':'I know dimensions'}</button>
                                 <button type="button" onClick={()=>updateField('constructionVolumeInputType', 'total')} className={`flex-1 border p-3 text-sm transition-all ${formData.constructionVolumeInputType==='total'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الحجم الكلي':'I know total volume'}</button>
                               </div>
                               {formData.constructionVolumeInputType==='dimensions' ? (
                                 <div className="space-y-3">
                                   {(formData.constructionPackages||[]).map((row,idx)=> (
                                     <div key={idx} className="grid grid-cols-12 gap-3 items-end">
                                       <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="L (cm)" value={row.length} onChange={(e)=>{ const rows=[...formData.constructionPackages]; rows[idx]={...rows[idx], length:e.target.value}; updateField('constructionPackages', rows as any); }} />
                                       <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="W (cm)" value={row.width} onChange={(e)=>{ const rows=[...formData.constructionPackages]; rows[idx]={...rows[idx], width:e.target.value}; updateField('constructionPackages', rows as any); }} />
                                       <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="H (cm)" value={row.height} onChange={(e)=>{ const rows=[...formData.constructionPackages]; rows[idx]={...rows[idx], height:e.target.value}; updateField('constructionPackages', rows as any); }} />
                                       <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="Qty" value={row.qty} onChange={(e)=>{ const rows=[...formData.constructionPackages]; rows[idx]={...rows[idx], qty:e.target.value}; updateField('constructionPackages', rows as any); }} />
                                       <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>{ const rows=[...formData.constructionPackages]; rows.splice(idx,1); updateField('constructionPackages', rows as any); }} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                     </div>
                                   ))}
                                   <button type="button" onClick={()=>updateField('constructionPackages', [...(formData.constructionPackages||[]), { length:'', width:'', height:'', qty:'' }] as any)} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة صف':'Add row'}</button>
                                 </div>
                               ) : (
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الحجم الكلي (CBM)':'Total Volume (CBM)'}</label>
                                   <input type="number" step="0.001" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="0.000" value={formData.constructionTotalVolume} onChange={(e)=>updateField('constructionTotalVolume', e.target.value)} />
                                 </div>
                               )}
                             </div>

                             
                          </div>
                       )}

                       {/* DG note */}
                       {(() => {
                          const anyDG = formData.hasDangerousGoods ||
                            (formData.dangerousAnyLiquids && formData.dangerousAnyLiquids !== 'no') ||
                            (formData.dangerousAnyPressurized && formData.dangerousAnyPressurized !== 'no') ||
                            (formData.dangerousAnyBatteries && formData.dangerousAnyBatteries !== 'no');
                          return anyDG ? (
                            <div className="mt-3 p-3 rounded border bg-amber-50 border-amber-200 text-amber-800 text-xs">
                              {lang==='ar' ? 'ملاحظة: البضائع الخطرة قد تؤثر على التوجيه والتسعير خصوصًا في الشحن الجوي.' : 'Note: Dangerous goods may affect routing and pricing, especially by air.'}
                            </div>
                          ) : null;
                       })()}

                       {/* Construction materials fields (multiple groups) */}
                       {formData.cargoCategory === 'construction' && (
                        <div ref={constructionRef} className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                          {(formData.constructionItems || []).length === 0 && (
                            <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                              <div className="mb-2 text-sm">{lang==='ar'?'أضف أول مجموعة مواد':'Add your first material group'}</div>
                              <button type="button" onClick={addConstructionItem} className="text-xs px-4 py-2 border border-stone-300 rounded hover:border-greenline-900 hover:text-greenline-900 transition">{lang==='ar'?'إضافة مجموعة':'Add material group'}</button>
                            </div>
                          )}
                          {(formData.constructionItems || []).length > 0 && (formData.constructionItems || []).map((c, cIdx) => (
                             <div key={cIdx} className="space-y-4 p-4 border border-stone-200 rounded-lg bg-stone-50">
                               <div className="flex items-center justify-between">
                                 <h4 className="text-sm font-medium text-stone-700">{lang==='ar'?'مجموعة مواد':'Material group'} #{cIdx+1}</h4>
                                 <button type="button" className="text-stone-500 hover:text-red-600 text-sm" onClick={()=>removeConstructionItem(cIdx)}>Remove</button>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'نوع المادة':'Material Type'}</label>
                                   <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={c.constructionMaterialType} onChange={(e)=>updateConstructionItem(cIdx,'constructionMaterialType', e.target.value)}>
                                     <option value="">{lang==='ar'?'اختر':'Select'}</option>
                                     <option value="tiles">{lang==='ar'?'بلاط':'Tiles'}</option>
                                     <option value="steel">{lang==='ar'?'فولاذ':'Steel'}</option>
                                     <option value="pipes">{lang==='ar'?'أنابيب':'Pipes'}</option>
                                     <option value="cement">{lang==='ar'?'أسمنت':'Cement'}</option>
                                     <option value="timber">{lang==='ar'?'خشب':'Timber'}</option>
                                     <option value="other">{lang==='ar'?'أخرى':'Other'}</option>
                                   </select>
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'التغليف':'Packaging'}</label>
                                   <select className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent appearance-none cursor-pointer" value={c.constructionPackaging} onChange={(e)=>updateConstructionItem(cIdx,'constructionPackaging', e.target.value)}>
                                     <option value="">{lang==='ar'?'اختر':'Select'}</option>
                                     <option value="loose">{lang==='ar'?'سائب':'Loose'}</option>
                                     <option value="bundles">{lang==='ar'?'حزم':'Bundles'}</option>
                                     <option value="pallets">{lang==='ar'?'بالتات':'Pallets'}</option>
                                     <option value="big-bags">{lang==='ar'?'أكياس كبيرة':'Big bags'}</option>
                                   </select>
                                 </div>
                               </div>
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الوزن الكلي (كجم)':'Total Weight (kg)'}</label>
                                   <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder="0" value={c.constructionTotalWeight} onChange={(e)=>updateConstructionItem(cIdx,'constructionTotalWeight', e.target.value)} />
                                 </div>
                                 <div className="space-y-2">
                                   <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'طول أقصى (م)':'Longest length (m)'}</label>
                                   <input type="number" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder="0" value={c.constructionLongestItemLength} onChange={(e)=>updateConstructionItem(cIdx,'constructionLongestItemLength', e.target.value)} />
                                 </div>
                               </div>
                               <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                                 <button type="button" onClick={()=>updateConstructionItem(cIdx,'constructionWeatherSensitive', !c.constructionWeatherSensitive)} className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${c.constructionWeatherSensitive ? 'bg-greenline-900 border-greenline-900' : 'border-stone-300'}`}>{c.constructionWeatherSensitive && <Check size={14} className="text-white" />}</button>
                                 <span className="text-xs text-stone-700">{lang==='ar'?'حساسة للطقس (للمجموعة)':'Weather sensitive (this group)'}</span>
                               </div>
                               <div className="space-y-4">
                                 <div className="flex gap-4">
                                   <button type="button" onClick={()=>updateConstructionItem(cIdx,'constructionVolumeInputType','dimensions')} className={`flex-1 border p-3 text-sm transition-all ${c.constructionVolumeInputType==='dimensions'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الأبعاد':'I know dimensions'}</button>
                                   <button type="button" onClick={()=>updateConstructionItem(cIdx,'constructionVolumeInputType','total')} className={`flex-1 border p-3 text-sm transition-all ${c.constructionVolumeInputType==='total'?'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>{lang==='ar'?'أعرف الحجم الكلي':'I know total volume'}</button>
                                 </div>
                                 {c.constructionVolumeInputType==='dimensions' ? (
                                   <div className="space-y-3">
                                     {(c.constructionPackages||[]).map((row,idx)=> (
                                       <div key={idx} className="grid grid-cols-12 gap-3 items-end">
                                         <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="L (cm)" value={row.length} onChange={(e)=>{ const rows=[...c.constructionPackages]; rows[idx]={...rows[idx], length:e.target.value}; updateConstructionItem(cIdx,'constructionPackages', rows as any); }} />
                                         <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="W (cm)" value={row.width} onChange={(e)=>{ const rows=[...c.constructionPackages]; rows[idx]={...rows[idx], width:e.target.value}; updateConstructionItem(cIdx,'constructionPackages', rows as any); }} />
                                         <input className="col-span-3 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="H (cm)" value={row.height} onChange={(e)=>{ const rows=[...c.constructionPackages]; rows[idx]={...rows[idx], height:e.target.value}; updateConstructionItem(cIdx,'constructionPackages', rows as any); }} />
                                         <input className="col-span-2 w-full border-b border-stone-200 py-3 bg-transparent" type="number" placeholder="Qty" value={row.qty} onChange={(e)=>{ const rows=[...c.constructionPackages]; rows[idx]={...rows[idx], qty:e.target.value}; updateConstructionItem(cIdx,'constructionPackages', rows as any); }} />
                                         <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>{ const rows=[...c.constructionPackages]; rows.splice(idx,1); updateConstructionItem(cIdx,'constructionPackages', rows as any); }} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                       </div>
                                     ))}
                                     <button type="button" onClick={()=>updateConstructionItem(cIdx,'constructionPackages', [...(c.constructionPackages||[]), { length:'', width:'', height:'', qty:'' }] as any)} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة صف':'Add row'}</button>
                                   </div>
                                 ) : (
                                   <div className="space-y-2">
                                     <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الحجم الكلي (CBM)':'Total Volume (CBM)'}</label>
                                     <input type="number" step="0.001" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder="0.000" value={c.constructionTotalVolume} onChange={(e)=>updateConstructionItem(cIdx,'constructionTotalVolume', e.target.value)} />
                                   </div>
                                 )}
                               </div>
                             </div>
                           ))}
                           {(formData.constructionItems || []).length > 0 && (
                             <div className="flex items-center justify-end">
                               <button type="button" onClick={addConstructionItem} className="text-sm text-greenline-900 hover:underline">{lang==='ar'?'إضافة مجموعة أخرى':'Add another material group'}</button>
                             </div>
                           )}
                         </div>
                       )}

                       {/* Dangerous goods fields */}
                       {formData.cargoCategory === 'dangerous' && (
                         <div ref={dangerousRef} className="space-y-6 animate-fade-up pt-4 border-t border-stone-100">
                           <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'أي بطاريات؟':'Any batteries?'}</label>
                             <div className="grid grid-cols-3 gap-3">
                               {['no','yes','not-sure'].map(val => (
                                 <button key={val} type="button" onClick={()=>updateField('dangerousAnyBatteries', val as 'no'|'yes'|'not-sure'|'')} className={`border p-3 text-xs transition-all text-center ${formData.dangerousAnyBatteries===val? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>
                                   {val==='no'?(lang==='ar'?'لا':'No'):val==='yes'?(lang==='ar'?'نعم':'Yes'):(lang==='ar'?'غير متأكد':'Not sure')}
                                 </button>
                               ))}
                             </div>
                           </div>

                           <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'أي سوائل/كيماويات؟':'Any liquids/chemicals?'}</label>
                             <div className="grid grid-cols-3 gap-3">
                               {['no','yes','not-sure'].map(val => (
                                 <button key={val} type="button" onClick={()=>updateField('dangerousAnyLiquids', val as 'no'|'yes'|'not-sure'|'')} className={`border p-3 text-xs transition-all text-center ${formData.dangerousAnyLiquids===val? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>
                                   {val==='no'?(lang==='ar'?'لا':'No'):val==='yes'?(lang==='ar'?'نعم':'Yes'):(lang==='ar'?'غير متأكد':'Not sure')}
                                 </button>
                               ))}
                             </div>
                           </div>

                           <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'أي مواد مضغوطة/غاز؟':'Any pressurised items (aerosols/gas)?'}</label>
                             <div className="grid grid-cols-3 gap-3">
                               {['no','yes','not-sure'].map(val => (
                                 <button key={val} type="button" onClick={()=>updateField('dangerousAnyPressurized', val as 'no'|'yes'|'not-sure'|'')} className={`border p-3 text-xs transition-all text-center ${formData.dangerousAnyPressurized===val? 'border-greenline-900 bg-greenline-900 text-white':'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'}`}>
                                   {val==='no'?(lang==='ar'?'لا':'No'):val==='yes'?(lang==='ar'?'نعم':'Yes'):(lang==='ar'?'غير متأكد':'Not sure')}
                                 </button>
                               ))}
                             </div>
                           </div>

                           <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                             <span className="text-sm text-stone-700">{lang==='ar'?'SDS متوفر؟':'SDS available?'}</span>
                             <button type="button" onClick={()=>updateField('dangerousSDSAvailable', !formData.dangerousSDSAvailable)} className={`w-10 h-5 rounded-full transition-all ${formData.dangerousSDSAvailable?'bg-greenline-900':'bg-stone-300'}`}>
                               <div className={`w-4 h-4 rounded-full bg-white transition-transform ${formData.dangerousSDSAvailable?'translate-x-5':'translate-x-0.5'}`} />
                             </button>
                           </div>

                           {/* Dangerous items list with quantities */}
                           {(formData.dangerousAnyBatteries !== 'no' || formData.dangerousAnyLiquids !== 'no' || formData.dangerousAnyPressurized !== 'no') && (
                             <div className="space-y-3">
                               <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{lang==='ar'?'تفاصيل المواد الخطرة':'Dangerous items details'}</div>
                               {(formData.dangerousItems||[]).length === 0 ? (
                                 <div className="p-6 border-2 border-dashed border-stone-300 rounded-lg text-center text-stone-600">
                                   <div className="mb-3 text-sm">{lang==='ar'?'أضف أول عنصر خطِر':'Add your first dangerous item'}</div>
                                   <div className="flex flex-wrap justify-center gap-3">
                                     <button type="button" onClick={()=>addDangerousItem('battery')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة بطاريات':'Add batteries'}</button>
                                     <button type="button" onClick={()=>addDangerousItem('liquid')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة سوائل/كيماويات':'Add liquids/chemicals'}</button>
                                     <button type="button" onClick={()=>addDangerousItem('pressurized')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة مواد مضغوطة':'Add pressurised items'}</button>
                                     <button type="button" onClick={()=>addDangerousItem('other')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة نوع آخر':'Add other item'}</button>
                                   </div>
                                 </div>
                               ) : (
                                 <div className="flex flex-wrap gap-3">
                                   <button type="button" onClick={()=>addDangerousItem('battery')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة بطاريات':'Add batteries'}</button>
                                   <button type="button" onClick={()=>addDangerousItem('liquid')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة سوائل/كيماويات':'Add liquids/chemicals'}</button>
                                   <button type="button" onClick={()=>addDangerousItem('pressurized')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة مواد مضغوطة':'Add pressurised items'}</button>
                                   <button type="button" onClick={()=>addDangerousItem('other')} className="text-xs border px-3 py-2 hover:border-greenline-900">{lang==='ar'?'إضافة نوع آخر':'Add other item'}</button>
                                 </div>
                               )}
                               {(formData.dangerousItems||[]).map((d, idx)=> (
                                 <div key={idx} className="grid grid-cols-12 gap-3 items-end p-3 bg-stone-50 rounded-lg">
                                   <div className="col-span-4">
                                     {d.type==='other' ? (
                                       <>
                                         <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'نوع العنصر':'Item type'}</label>
                                         <input type="text" className="w-full border-b border-stone-200 py-2 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'اذكر النوع':'Specify type'} value={d.customType||''} onChange={(e)=>updateDangerousItem(idx,'customType', e.target.value)} />
                                       </>
                                     ) : (
                                       <div className="text-sm text-stone-700">{d.type==='battery'?'Batteries':d.type==='liquid'?'Liquids/Chemicals':'Pressurised (gas/aerosols)'}</div>
                                     )}
                                   </div>
                                   <div className="col-span-3">
                                     <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'الكمية':'Quantity'}</label>
                                     <input type="number" className="w-full border-b border-stone-200 py-2 text-base focus:outline-none focus:border-greenline-900 bg-transparent" value={d.quantity} onChange={(e)=>updateDangerousItem(idx,'quantity', e.target.value)} />
                                   </div>
                                   <div className="col-span-4">
                                     <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'ملاحظة (اختياري)':'Note (optional)'}</label>
                                     <input type="text" className="w-full border-b border-stone-200 py-2 text-base focus:outline-none focus:border-greenline-900 bg-transparent" placeholder={lang==='ar'?'وصف مختصر':'Short description'} value={d.note||''} onChange={(e)=>updateDangerousItem(idx,'note', e.target.value)} />
                                   </div>
                                   <div className="col-span-1 flex justify-end"><button type="button" onClick={()=>removeDangerousItem(idx)} className="text-stone-500 hover:text-red-600 text-sm">×</button></div>
                                 </div>
                               ))}
                             </div>
                           )}
                         </div>
                       )}

                       

                       {/* Optional (shared for all categories) */}
                       <div className="grid grid-cols-1 gap-6 pt-4 border-t border-stone-100">
                         <div className="space-y-2">
                           <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{lang==='ar'?'قيمة البضاعة (اختياري)':'Cargo Value (Optional)'}</label>
                           <input type="text" className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent" placeholder={lang==='ar'?'مثال: $10,000':'e.g. $10,000'} value={formData.cargoValue} onChange={(e)=>updateField('cargoValue', e.target.value)} />
                         </div>
                       </div>
                    </div>
                 )}
                  </>
                )}

                {/* Step 4 - Service Scope (currently disabled) */}
                {false && step === 4 && (
                  <div className="space-y-6">
                    <div className="mb-2">
                      <h3 className="font-serif text-lg text-luxury-black">
                        {lang === 'ar' ? 'نطاق الخدمة' : 'Service Scope'}
                      </h3>
                      <p className="text-sm text-stone-500">
                        {lang === 'ar'
                          ? 'هذه الخيارات تحدد نطاق العمل الأساسي وتؤثر على السعر'
                          : 'These options define your job scope and can affect pricing.'}
                      </p>
                    </div>

                    {/* Pickup */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                        {lang === 'ar' ? 'الاستلام من المنشأ؟' : 'Pickup needed at origin?'}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['yes', 'no', 'not-sure'] as const).map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updateField('pickupNeeded', v)}
                            className={`border p-3 text-xs transition-all text-center ${
                              formData.pickupNeeded === v
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            {lang === 'ar'
                              ? v === 'yes'
                                ? 'نعم'
                                : v === 'no'
                                  ? 'لا'
                                  : 'غير متأكد'
                              : v === 'yes'
                                ? 'Yes'
                                : v === 'no'
                                  ? 'No'
                                  : 'Not sure'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Delivery */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                        {lang === 'ar' ? 'التسليم في الوجهة؟' : 'Delivery needed at destination?'}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['yes', 'no', 'not-sure'] as const).map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updateField('deliveryNeeded', v)}
                            className={`border p-3 text-xs transition-all text-center ${
                              formData.deliveryNeeded === v
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            {lang === 'ar'
                              ? v === 'yes'
                                ? 'نعم'
                                : v === 'no'
                                  ? 'لا'
                                  : 'غير متأكد'
                              : v === 'yes'
                                ? 'Yes'
                                : v === 'no'
                                  ? 'No'
                                  : 'Not sure'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Customs */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                        {lang === 'ar' ? 'التخليص الجمركي؟' : 'Customs clearance needed?'}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['yes', 'no', 'not-sure'] as const).map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updateField('customsClearanceNeeded', v)}
                            className={`border p-3 text-xs transition-all text-center ${
                              formData.customsClearanceNeeded === v
                                ? 'border-greenline-900 bg-greenline-900 text-white'
                                : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                            }`}
                          >
                            {lang === 'ar'
                              ? v === 'yes'
                                ? 'نعم'
                                : v === 'no'
                                  ? 'لا'
                                  : 'غير متأكد'
                              : v === 'yes'
                                ? 'Yes'
                                : v === 'no'
                                  ? 'No'
                                  : 'Not sure'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Optional services (collapsible) */}
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => setOptionalSectionOpen((o) => !o)}
                        className="w-full flex items-center justify-between p-4 border border-stone-200 rounded-lg hover:border-greenline-900 transition"
                      >
                        <span className="text-sm font-medium text-stone-700">
                          {lang === 'ar' ? 'خدمات إضافية (اختياري)' : 'Optional services'}
                        </span>
                        {optionalSectionOpen ? (
                          <ChevronUp size={18} className="text-stone-600" />
                        ) : (
                          <ChevronDown size={18} className="text-stone-600" />
                        )}
                      </button>

                      {optionalSectionOpen && (
                        <div className="mt-3 p-4 border border-stone-200 rounded-lg bg-stone-50 space-y-3">
                          {(
                            [
                              { key: 'insurance', en: 'Insurance', ar: 'تأمين' },
                              { key: 'crating', en: 'Crating', ar: 'تغليف خشبي' },
                              { key: 'warehousing', en: 'Warehousing', ar: 'تخزين' },
                              { key: 'photoInspection', en: 'Photo inspection', ar: 'فحص بالصور' },
                              { key: 'specialHandling', en: 'Special handling', ar: 'مناولة خاصة' },
                              { key: 'craneService', en: 'Crane service', ar: 'رافعة' },
                              { key: 'lowbedService', en: 'Lowbed service', ar: 'لوبد' },
                            ] as const
                          ).map((s) => {
                            const k = s.key as keyof QuoteFormData;
                            const checked = Boolean(formData[k]);
                            return (
                              <div key={s.key} className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-200">
                                <span className="text-sm text-stone-700">{lang === 'ar' ? s.ar : s.en}</span>
                                <button
                                  type="button"
                                  onClick={() => updateField(k, (!checked as any))}
                                  className={`w-10 h-5 rounded-full transition-all ${checked ? 'bg-greenline-900' : 'bg-stone-300'}`}
                                >
                                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 5 - Timing */}
               {step === 5 && (
                    <>
                       <p className="mb-6 text-sm text-stone-600 leading-relaxed">
                         {lang === 'ar'
                           ? 'حدِّد تفضيلات التوقيت: أدخِل تاريخ جاهزية البضاعة، واختر أولوية الشحن (الأرخص، متوازن، الأسرع)، ويمكنك إضافة تاريخ تسليم مستهدف (اختياري). نستخدم هذه المعلومات لمواءمة المسارات والجداول.'
                           : 'Define your timing preferences: set the cargo ready date, choose your priority (cheapest, balanced, fastest), and optionally add a target delivery date. We use this to align routing and schedules.'}
                       </p>
                       <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'تاريخ جاهزية البضاعة' : 'Cargo Ready Date'}
                          </label>
                          <input 
                            type="date" 
                            className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                            value={formData.cargoReadyDate}
                            onChange={(e) => updateField('cargoReadyDate', e.target.value)}
                          />
                       </div>

                       <div className="space-y-4">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'الأولوية' : 'Priority'}
                          </label>
                          <div className="grid grid-cols-3 gap-4">
                             {[
                               { value: 'cheapest', label: lang === 'ar' ? 'الأرخص' : 'Cheapest', desc: lang === 'ar' ? 'أطول وقت' : 'Longest Time' },
                               { value: 'balanced', label: lang === 'ar' ? 'متوازن' : 'Balanced', desc: lang === 'ar' ? 'وقت وسعر معتدل' : 'Moderate Time & Price' },
                               { value: 'fastest', label: lang === 'ar' ? 'الأسرع' : 'Fastest', desc: lang === 'ar' ? 'أعلى سعر' : 'Highest Price' },
                             ].map((priority) => (
                               <button
                                 key={priority.value}
                                 type="button"
                                 onClick={() => updateField('priority', priority.value as Priority)}
                                 className={`border p-4 transition-all text-center ${
                                   formData.priority === priority.value
                                     ? 'border-greenline-900 bg-greenline-900 text-white'
                                     : 'border-stone-200 hover:border-greenline-900 hover:bg-stone-50'
                                 }`}
                               >
                                    <div className="font-medium mb-1">{priority.label}</div>
                                    <div className="text-xs opacity-70">{priority.desc}</div>
                               </button>
                             ))}
                          </div>
                       </div>

                       <div className="space-y-2 pt-4 border-t border-stone-100">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'تاريخ التسليم المستهدف (اختياري)' : 'Target Delivery Date (Optional)'}
                          </label>
                          <input 
                            type="date" 
                            className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                            value={formData.targetDeliveryDate}
                            onChange={(e) => updateField('targetDeliveryDate', e.target.value)}
                          />
                       </div>
                    </>
                 )}

                {/* Step 6 - Contact */}
               {step === 6 && (
                    <>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'الاسم الكامل' : 'Full Name'} *
                             </label>
                             <input 
                               type="text" 
                               className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                               placeholder={lang === 'ar' ? 'اسمك الكامل' : 'Your Full Name'}
                               value={formData.fullName}
                               onChange={(e) => updateField('fullName', e.target.value)}
                             />
                          </div>
                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'الشركة (اختياري)' : 'Company (Optional)'}
                             </label>
                             <input 
                               type="text" 
                               className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                               placeholder={lang === 'ar' ? 'اسم الشركة' : 'Company Name'}
                               value={formData.company}
                               onChange={(e) => updateField('company', e.target.value)}
                             />
                          </div>
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'رقم الهاتف / واتساب' : 'Phone / WhatsApp'} *
                             </label>
                             <input 
                               type="tel" 
                               className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                               placeholder="+218 ..."
                               value={formData.phone}
                               onChange={(e) => updateField('phone', e.target.value)}
                             />
                          </div>
                          <div className="space-y-2">
                             <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                               {lang === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (Optional)'}
                             </label>
                             <input 
                               type="email" 
                               className="w-full border-b border-stone-200 py-3 text-base focus:outline-none focus:border-greenline-900 transition-colors bg-transparent"
                               placeholder="email@company.com"
                               value={formData.email}
                               onChange={(e) => updateField('email', e.target.value)}
                             />
                          </div>
                       </div>

                       <div className="space-y-2">
                          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                            {lang === 'ar' ? 'ملاحظات إضافية' : 'Additional Notes'}
                          </label>
                          <textarea 
                            rows={4} 
                            className="w-full border border-stone-200 p-4 text-sm focus:outline-none focus:border-greenline-900 transition-colors bg-transparent resize-none"
                            placeholder={lang === 'ar' ? 'أي متطلبات خاصة...' : 'Any special requirements...'}
                            value={formData.notes}
                            onChange={(e) => updateField('notes', e.target.value)}
                          />
                       </div>
                    </>
                 )}

                 {/* Navigation Buttons */}
                 <div className="pt-8 flex justify-between items-center">
                    <button 
                      onClick={prevStep} 
                      disabled={step === 1}
                      className={`text-xs uppercase tracking-[0.2em] transition-colors flex items-center gap-2 ${
                        step === 1 ? 'text-stone-300 cursor-not-allowed' : 'text-stone-400 hover:text-luxury-black'
                      }`}
                    >
                       <ArrowLeft size={16} className={isRTL ? 'rotate-180' : ''} />
                       {lang === 'ar' ? 'رجوع' : 'Back'}
                    </button>
                    
                    {step < totalSteps ? (
                       <button 
                         onClick={nextStep} 
                         className="bg-luxury-black text-white px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-greenline-900 transition-colors flex items-center gap-3"
                       >
                          {lang === 'ar' ? 'التالي' : 'Continue'} <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''}/>
                       </button>
                    ) : (
                       <button 
                         onClick={handleSubmit} 
                         className="bg-greenline-900 text-white px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-greenline-800 transition-colors flex items-center gap-3"
                       >
                          {lang === 'ar' ? 'إرسال الطلب' : 'Submit Request'} <Send size={16} className={isRTL ? 'rotate-180' : ''}/>
                       </button>
                    )}
                 </div>
              </form>
            </div>
        </div>
      </div>
    </div>
  );
};
